import React, { CSSProperties, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import DataTableNew from '../../../components/main/DataTableNew/DataTableNew';
import DeleteContentsDialog from '../../../components/dialogs/DeleteContentsDialog/DeleteContentsDialog';
import { ContentFilterInput, ContentSortInput, LibraryCacheStatus, ProjectSortInput, Tag, UserRoleEnum, useAssignContentsClassificationsMutation, useAssignContentsTagsMutation, useEditLibraryDescriptionMutation, useGetCategoryListQuery, useGetContentHistoryBadgesQuery, useGetDirectContentsQuery, useGetInheritedLibrariesQuery, useGetLibraryCacheQuery, useGetLibraryCustomContentsQuery, useGetLibraryProjectsQuery, useGetLibraryQuery, useGetTagListWithTypeQuery, useGetUnregisteredContentsQuery, useRenameLibraryMutation, useSetRelatedContentsMutation } from '../../../generated/graphql';
import { useParams } from 'react-router-dom';
import InheritLibraryDialog from '../../../components/dialogs/InheritLibraryDialog/InheritLibraryDialog';
import { capitalizeFirstLetter, ticksToDate } from '../../../Utils';
import { useCurrentUser, useDownloadAttachmentBytesWithProgress, useDownloadAttachmentWithProgress, useLoadHttpClient, useRoleOverride } from '../../../App.hooks';
import UnInheritLibraryDialog from '../../../components/dialogs/UnInheritLibraryDialog/UnInheritLibraryDialog';
import ShowOnRole from '../../../components/main/ShowOnRole/ShowOnRole';
import SpinnerNew from '../../../components/main/SpinnerNew/SpinnerNew';
import { ClientContext } from '../Clients.Provider';
import { createGraphQLOrder, TableSort } from '../../../components/main/DataTableNew/DataTableSorting';

import EssentialsNew from '../../../components/main/EssentialsNew/EssentialsNew';
import AddContentsTagsDialog from '../../../components/dialogs/AddContentsTagsDialog/AddContentsTagsDialog';
import ImageViewerButton from '../../../components/main/ImageViewerButton/ImageViewerButton';
import RegisterContentsDialog from '../../../components/dialogs/RegisterContentsDialog/RegisterContentsDialog';
import AddCustomContentsDialog from '../../../components/dialogs/AddCustomContentsDialog/AddCustomContentsDialog';

import EditCustomContentSettingsDialog from '../../../components/dialogs/EditCustomContentSettingsDialog/EditCustomContentSettingsDialog';
import DeleteCustomContentsDialog from '../../../components/dialogs/DeleteCustomContentsDialog/DeleteCustomContentsDialog';

import NewLibraryAdminDialog from '../../../components/dialogs/NewLibraryAdminDialog/NewLibraryAdminDialog';
import DeleteLibraryAdminDialog from '../../../components/dialogs/DeleteLibraryAdminDialog/DeleteLibraryAdminDialog';
import MoveLibraryDialog from '../../../components/dialogs/MoveLibraryDialog/MoveLibraryDialog';
import MoveContentsDialog from '../../../components/dialogs/MoveContentsDialog/MoveContentsDialog';
import AddContentsGroupsDialog from '../../../components/dialogs/AddContentsGroupsDialog/AddContentsGroupsDialog';
import NewContentDialog from '../../../components/dialogs/NewContentDialog/NewContentDialog';
import BulkThumbnailUploadDialog from '../../../components/dialogs/BulkThumbnailUploadDialog/BulkThumbnailUploadDialog';
import AddContentsClassificationsDialog from '../../../components/dialogs/AddContentsClassificationsDialog/AddContentsClassificationsDialog';
import ContentDetailDialog from '../../../components/dialogs/ContentDetailDialog/ContentDetailDialog';
import MapClassificationDialog from '../../../components/dialogs/MapClassificationDialog/MapClassificationDialog';
import CompareContentsDialog from '../../../components/dialogs/CompareContentsDialog/CompareContentsDialog';
import { Plus, Trash2, Pencil, ArrowRight, FolderPlus, Tag as TagIcon, Layers, Loader2, FileText, FileInput, Library as LibraryIcon, FolderKanban, Shield, PackageOpen, HardDrive, Clock, CheckCircle2, Hash, FolderTree, AppWindow, AlertCircle, UserCircle, ClipboardPaste, Search, Maximize2, Minimize2, GitCompareArrows, X, Bug, HelpCircle, Lightbulb, MessageCircle, CircleCheckBig, Copy, Image as ImageIcon, RotateCcw, Server } from 'lucide-react';
import RefreshButton from '../../../components/main/RefreshButton/RefreshButton';
import AddLibrariesTagsDialog from '../../../components/dialogs/AddLibrariesTagsDialog/AddLibrariesTagsDialog';
import DeleteLibraryTagDialog from '../../../components/dialogs/DeleteLibraryTagDialog/DeleteLibraryTagDialog';
import { extractTypeMark } from './libraryCacheUtils';

const ClassificationsCell = ({ row }: { row: any }) => {
    return (
        <div className="flex flex-wrap gap-1">
            {(row?.classifications as any[])?.map((c: any, i: number) => (
                <span key={i} className="badge badge-default text-xs whitespace-nowrap">
                    <span className="font-semibold opacity-80">{c?.type?.name}</span>
                    <span className="font-semibold opacity-80 mr-1">:</span>
                    <span>{c?.name}</span>
                </span>
            ))}
        </div>
    );
};

const LIBRARY_CACHE_URI: string = process.env.REACT_APP_LIBRARY_CACHE_URI || "";

const REQUEST_CONTENT_FILE_DOWNLOAD = gql`
    mutation requestContentFileDownloadFromLibrary($contentId: String) {
        Content(id: $contentId) {
            requestFileDownload
        }
    }
`;

const RESET_LIBRARY_CONTENT_USAGE_COUNTS = gql`
    mutation resetLibraryContentUsageCountsFromLibrary($libraryId: String) {
        Library(id: $libraryId) {
            resetContentUsageCounts
        }
    }
`;

const MetricCard = ({ icon: Icon, label, value, sub, color }: { icon: any; label: string; value: React.ReactNode; sub?: React.ReactNode; color: string }) => (
    <div className="card p-4 flex items-start gap-3 min-w-0">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: color + '18' }}>
            <Icon size={18} style={{ color }} />
        </div>
        <div className="min-w-0">
            <p className="text-xs font-medium mb-0.5" style={{ color: 'var(--text-tertiary)' }}>{label}</p>
            <p className="text-lg font-semibold leading-tight" style={{ color: 'var(--text-primary)' }}>{value}</p>
            {sub && <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--text-tertiary)' }}>{sub}</p>}
        </div>
    </div>
);

/** Live elapsed‑time counter – ticks every 100ms while `running` is true. */
const useElapsedTimer = (running: boolean) => {
    const startRef = useRef<number>(performance.now());
    const [elapsed, setElapsed] = useState(0);

    useEffect(() => {
        if (running) {
            startRef.current = performance.now();
            setElapsed(0);
            const id = setInterval(() => setElapsed(performance.now() - startRef.current), 100);
            return () => clearInterval(id);
        }
    }, [running]);

    return elapsed;
};

/** Animates a number from 0 to `end` over `duration` ms using ease‑out. */
const AnimatedNumber = ({ end, duration = 800, formatter }: { end: number; duration?: number; formatter?: (n: number) => string }) => {
    const [display, setDisplay] = useState(0);
    const rafRef = useRef<number>();

    useEffect(() => {
        if (end === 0) { setDisplay(0); return; }
        const start = performance.now();
        const tick = (now: number) => {
            const t = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - t, 3);           // ease-out cubic
            setDisplay(Math.round(eased * end));
            if (t < 1) rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    }, [end, duration]);

    return <>{formatter ? formatter(display) : display.toLocaleString()}</>;
};

type LibraryCacheManifestPayload = {
    schemaVersion: number;
    contentSha256: string;
    payloadEncoding: string;
};

const sha256Hex = async (payload: ArrayBuffer) => {
    const hash = await window.crypto.subtle.digest('SHA-256', payload);
    return Array.from(new Uint8Array(hash))
        .map(value => value.toString(16).padStart(2, '0'))
        .join('');
};

const decompressGzipJson = async (payload: ArrayBuffer) => {
    const DecompressionStreamConstructor = (window as any).DecompressionStream;
    if (!DecompressionStreamConstructor) {
        throw new Error('This browser does not support gzip decompression.');
    }

    const compressedStream = new Blob([payload]).stream();
    const decompressedStream = compressedStream.pipeThrough(new DecompressionStreamConstructor('gzip'));
    return new Response(decompressedStream as any).text();
};

const validateGzipManifest = async (manifestText: string, payload: ArrayBuffer) => {
    const manifest = JSON.parse(manifestText) as LibraryCacheManifestPayload;
    if (manifest.schemaVersion !== 1 || manifest.payloadEncoding !== 'json+gzip' || !manifest.contentSha256) {
        throw new Error('The compressed library cache manifest is not supported.');
    }

    const actualHash = await sha256Hex(payload);
    if (actualHash !== manifest.contentSha256.toLowerCase()) {
        throw new Error('The compressed library cache failed its integrity check.');
    }
};

const PreviewTab = ({ showCacheDiagnostics }: { showCacheDiagnostics: boolean }) => {
    const { clientId, libraryId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(true);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [data, setData] = useState<any[]>([]);
    const [searchText, setSearchText] = useState("");
    const [selectedContentId, setSelectedContentId] = useState<string | null>(
        searchParams.get('contentId')
    );
    const [openWithComments, setOpenWithComments] = useState(false);
    const [activityFilter, setActivityFilter] = useState<Set<string>>(new Set());
    const [sortField, setSortField] = useState<string>("Name");
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
    const [cacheMetrics, setCacheMetrics] = useState<{
        sizeMb: number;
        downloadTimeMs: number;
        totalCount: number;
        familyCount: number;
        categoryCount: number;
        applicationCount: number;
        cacheFormat: 'v1' | 'v2';
        fallbackReason?: string;
        status: string;
    } | null>(null);
    const [downloadProgress, setDownloadProgress] = useState<{
        loaded: number;
        total: number | undefined;
        percent: number | undefined;
    } | null>(null);
    const downloadLegacyCache = useDownloadAttachmentWithProgress();
    const downloadCompressedCache = useDownloadAttachmentBytesWithProgress();
    const elapsedMs = useElapsedTimer(isDownloading);

    const libraryMain = useGetLibraryQuery({ variables: { id: libraryId } });
    const library = useGetLibraryCacheQuery({
        variables: {
            id: libraryId
        }
    });

    // Activity badges
    const historyBadgesData = useGetContentHistoryBadgesQuery({
        variables: { libraryId: libraryId },
    });
    // Auto-paginate to fetch all history badge pages
    useEffect(() => {
        const pageInfo = historyBadgesData.data?.ContentVersionList?.pageInfo;
        if (pageInfo?.hasNextPage && pageInfo.endCursor) {
            historyBadgesData.fetchMore({
                variables: { libraryId, after: pageInfo.endCursor },
                updateQuery: (prev, { fetchMoreResult }) => {
                    if (!fetchMoreResult?.ContentVersionList?.nodes) return prev;
                    return {
                        ...prev,
                        ContentVersionList: {
                            ...fetchMoreResult.ContentVersionList,
                            nodes: [
                                ...(prev.ContentVersionList?.nodes || []),
                                ...fetchMoreResult.ContentVersionList.nodes,
                            ],
                        },
                    };
                },
            });
        }
    }, [historyBadgesData.data]);
    const badgeTagsQuery = useGetTagListWithTypeQuery({ variables: { contains: '', type: 'Feedback' } });
    const BADGE_CONFIG: Record<string, { color: string; bgColor: string; icon: typeof Bug }> = {
        bug:      { color: 'var(--danger)',  bgColor: 'var(--danger-soft, #fef2f2)',  icon: Bug },
        request:  { color: 'var(--info)',    bgColor: 'var(--info-soft, #eff6ff)',    icon: HelpCircle },
        feature:  { color: 'var(--success)', bgColor: 'var(--success-soft, #f0fdf4)', icon: Lightbulb },
        comment:  { color: 'var(--accent)',  bgColor: 'var(--accent-soft)',           icon: MessageCircle },
        resolved: { color: 'var(--success)', bgColor: 'var(--success-soft, #f0fdf4)', icon: CircleCheckBig },
    };
    const badgeTagIdToKeyMap = useMemo<Record<string, string>>(() => {
        const map: Record<string, string> = {};
        const nodes = badgeTagsQuery.data?.TagList?.nodes;
        if (!nodes) return map;
        for (const tag of nodes) {
            const key = tag?.name?.toLowerCase();
            if (key && BADGE_CONFIG[key] && tag?.id) {
                map[tag.id] = key;
            }
        }
        return map;
    }, [badgeTagsQuery.data]);
    const historyBadgeMap = useMemo(() => {
        const map = new Map<string, string>();
        const nodes = historyBadgesData.data?.ContentVersionList?.nodes;
        if (!nodes) return map;
        for (const version of nodes) {
            if (!version?.contentId || !version.histories) continue;
            let latestTime = -1;
            let latestCategory: string | null = null;
            for (const h of version.histories) {
                if (!h) continue;
                const time = h.createdTime ? Number(h.createdTime) : 0;
                if (h.tags) {
                    for (const tag of h.tags) {
                        const key = tag?.name?.toLowerCase();
                        if (key && BADGE_CONFIG[key] && tag?.type === 'Feedback' && time > latestTime) {
                            latestTime = time;
                            latestCategory = key;
                        }
                    }
                }
                if (!latestCategory || time > latestTime) {
                    if ((h as any).tagIds) {
                        for (const tid of (h as any).tagIds) {
                            const key = tid ? badgeTagIdToKeyMap[tid] : null;
                            if (key && time > latestTime) {
                                latestTime = time;
                                latestCategory = key;
                            }
                        }
                    }
                }
            }
            if (latestCategory) {
                map.set(version.contentId, latestCategory);
            }
        }
        return map;
    }, [historyBadgesData.data, badgeTagIdToKeyMap]);

    useEffect(() => {
        const cache = library.data?.Library;
        if (!library.data) return;

        if (!cache?.cacheJson && !cache?.caches) {
            setIsLoading(false);
            setIsDownloading(false);
            setIsProcessing(false);
            setCacheMetrics(null);
            return;
        }

        let cancelled = false;

        const loadCache = async () => {
            setIsLoading(true);
            setIsDownloading(true);
            setIsProcessing(false);
            setDownloadProgress(null);

            const startTime = performance.now();
            let rawJson: string | null = null;
            let sizeBytes = 0;
            let cacheFormat: 'v1' | 'v2' = 'v1';
            let fallbackReason = !cache.cacheJson || !cache.cacheJsonManifest
                ? 'Compressed cache metadata is unavailable.'
                : undefined;

            if (cache.cacheJson && cache.cacheJsonManifest) {
                try {
                    const manifestPayload = await downloadCompressedCache(cache.cacheJsonManifest);
                    const manifestText = new TextDecoder('utf-8').decode(manifestPayload);
                    const compressedPayload = await downloadCompressedCache(cache.cacheJson, progress => {
                        if (!cancelled) setDownloadProgress(progress);
                    });
                    await validateGzipManifest(manifestText, compressedPayload);
                    rawJson = await decompressGzipJson(compressedPayload);
                    sizeBytes = compressedPayload.byteLength;
                    cacheFormat = 'v2';
                } catch (error) {
                    fallbackReason = error instanceof Error
                        ? error.message
                        : 'The compressed cache could not be loaded.';
                    console.warn('Compressed library cache unavailable; falling back to the legacy cache.', error);
                }
            }

            if (!rawJson && cache.caches) {
                const legacyPayload = await downloadLegacyCache(cache.caches, progress => {
                    if (!cancelled) setDownloadProgress(progress);
                });
                rawJson = atob(legacyPayload);
                sizeBytes = legacyPayload.length;
            }

            if (!rawJson) {
                throw new Error('No supported library cache is available.');
            }

            const downloadTimeMs = performance.now() - startTime;
            if (cancelled) return;

            setIsDownloading(false);
            setIsProcessing(true);
            setDownloadProgress(null);

            // Yield so the processing state renders before JSON parsing and row expansion.
            await new Promise(resolve => setTimeout(resolve, 0));
            if (cancelled) return;

            const rawData = JSON.parse(rawJson);
            const parsedData = (rawData?.data?.ContentsBySecret?.nodes as any[]).flatMap(f => {
                return (f.types as any[]).length > 0 ? (f.types as any[]).flatMap(t => {
                    return {
                        _contentId: f.id,
                        Name: t.name,
                        Family: f.name,
                        Category: f.category,
                        Application: f.applicationName,
                        Settings: t.settings,
                        TypeMark: extractTypeMark(t.settings),
                    };
                }) : {
                    _contentId: f.id,
                    Name: (f.name as string)?.substring((f.name as string)?.indexOf("_") + 1) ?? "",
                    Family: (f.name as string)?.split("_")[0] ?? "",
                    Application: f.applicationName,
                    Category: f.category,
                    Settings: f.settings,
                    TypeMark: f.typeMark ?? "",
                };
            });
            const families = new Set(parsedData.map((r: any) => r.Family).filter(Boolean));
            const categories = new Set(parsedData.map((r: any) => r.Category).filter(Boolean));
            const applications = new Set(parsedData.map((r: any) => r.Application).filter(Boolean));
            setCacheMetrics({
                sizeMb: sizeBytes / (1024 * 1024),
                downloadTimeMs,
                totalCount: parsedData.length,
                familyCount: families.size,
                categoryCount: categories.size,
                applicationCount: applications.size,
                cacheFormat,
                fallbackReason: cacheFormat === 'v1' ? fallbackReason : undefined,
                status: 'Loaded',
            });
            setData(parsedData);
            setIsProcessing(false);
            setIsLoading(false);
        };

        loadCache().catch(error => {
            if (cancelled) return;
            console.error('Failed to load the library cache.', error);
            setCacheMetrics(prev => prev ? { ...prev, status: 'Failed' } : null);
            setIsDownloading(false);
            setIsProcessing(false);
            setIsLoading(false);
        });

        return () => {
            cancelled = true;
        };
    }, [library.data, downloadCompressedCache, downloadLegacyCache]);

    const columns = ['Family', 'Name', 'Category', 'Application', 'TypeMark'];

    const filtered = data.filter(row => {
        // Text search
        if (searchText && !columns.some(col => (row[col] ?? '').toString().toLowerCase().includes(searchText.toLowerCase()))) {
            return false;
        }
        // Activity type filter
        if (activityFilter.size > 0) {
            const category = row._contentId ? historyBadgeMap.get(row._contentId) : null;
            if (!category || !activityFilter.has(category)) return false;
        }
        return true;
    });

    const sorted = [...filtered].sort((a, b) => {
        const aVal = (a[sortField] ?? '').toString().toLowerCase();
        const bVal = (b[sortField] ?? '').toString().toLowerCase();
        return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });

    const handleSort = (field: string) => {
        if (sortField === field) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDir('asc');
        }
    };

    const cacheStatus = libraryMain.data?.Library?.cacheStatus;
    const cacheCreatedTime = libraryMain.data?.Library?.cacheCreatedTime;

    const statusConfig: Record<string, { label: string; color: string }> = {
        Loaded:    { label: 'Loaded',  color: '#15803d' },
        Failed:    { label: 'Failed',  color: '#b91c1c' },
    };
    const cacheStatusDisplay = (() => {
        if (isLoading) return { label: 'Downloading…', color: '#b45309' };
        if (cacheMetrics?.status === 'Failed') return statusConfig.Failed;
        if (cacheMetrics) return statusConfig.Loaded;
        if (cacheStatus === LibraryCacheStatus.Running || cacheStatus === LibraryCacheStatus.Pending) return { label: cacheStatus === LibraryCacheStatus.Running ? 'Building…' : 'Pending', color: '#b45309' };
        if (cacheStatus === LibraryCacheStatus.Failed) return { label: 'Build Failed', color: '#b91c1c' };
        if (cacheStatus === LibraryCacheStatus.None || !cacheStatus) return { label: 'Not Created', color: '#6b7280' };
        return { label: 'Ready', color: '#15803d' };
    })();

    const formatDownloadTime = (ms: number) => {
        if (ms < 1000) return `${Math.round(ms)}ms`;
        const secs = ms / 1000;
        if (secs < 60) return `${secs.toFixed(1)}s`;
        const mins = Math.floor(secs / 60);
        const remainSecs = Math.round(secs % 60);
        return `${mins}m ${remainSecs}s`;
    };

    const formatBytes = (bytes: number) => {
        if (bytes < 1024) return `${bytes} B`;
        const kb = bytes / 1024;
        if (kb < 1024) return `${kb.toFixed(0)} KB`;
        const mb = kb / 1024;
        return `${mb.toFixed(1)} MB`;
    };

    return (
        <div className="flex flex-col h-full">
            {isLoading ? (
                <>
                    {/* Metric cards with download progress */}
                    <div className={`grid grid-cols-2 md:grid-cols-3 ${showCacheDiagnostics ? 'xl:grid-cols-7' : 'lg:grid-cols-6'} gap-3 px-4 pt-4 pb-2`}>
                        <MetricCard
                            icon={Loader2}
                            label="Cache Status"
                            value={
                                <span style={{ color: '#b45309' }}>
                                    {isProcessing
                                        ? 'Processing…'
                                        : downloadProgress?.percent != null
                                            ? `Downloading… ${downloadProgress.percent}%`
                                            : 'Downloading…'}
                                </span>
                            }
                            sub={cacheCreatedTime ? ticksToDate(cacheCreatedTime) : undefined}
                            color="#b45309"
                        />
                        {showCacheDiagnostics && (
                            <MetricCard
                                icon={PackageOpen}
                                label="Cache Delivery"
                                value="Checking..."
                                sub="Selecting fastest cache"
                                color="#15803d"
                            />
                        )}
                        <MetricCard
                            icon={Hash}
                            label="Total Items"
                            value="—"
                            color="#6366f1"
                        />
                        <MetricCard
                            icon={HardDrive}
                            label="Downloaded"
                            value={downloadProgress
                                ? downloadProgress.total
                                    ? `${formatBytes(downloadProgress.loaded)} / ${formatBytes(downloadProgress.total)}`
                                    : formatBytes(downloadProgress.loaded)
                                : '—'}
                            color="#0ea5e9"
                        />
                        <MetricCard
                            icon={Clock}
                            label="Download Time"
                            value={formatDownloadTime(elapsedMs)}
                            color="#f59e0b"
                        />
                        <MetricCard
                            icon={FolderTree}
                            label="Categories"
                            value="—"
                            color="#8b5cf6"
                        />
                        <MetricCard
                            icon={AppWindow}
                            label="Applications"
                            value="—"
                            color="#ec4899"
                        />
                    </div>
                    {/* Progress bar */}
                    {downloadProgress && (
                        <div className="px-4 pb-2">
                            <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--surface-300)' }}>
                                <div
                                    className="h-full rounded-full transition-all duration-300"
                                    style={{
                                        width: downloadProgress.percent != null ? `${downloadProgress.percent}%` : '100%',
                                        backgroundColor: '#0ea5e9',
                                        ...(downloadProgress.percent == null ? {
                                            animation: 'indeterminate 1.5s infinite linear',
                                            width: '30%',
                                        } : {})
                                    }}
                                />
                            </div>
                        </div>
                    )}
                    <div className="flex-1 flex items-center justify-center">
                        <SpinnerNew />
                    </div>
                </>
            ) : (
                <>
                    {/* Metric cards */}
                    <div className={`grid grid-cols-2 md:grid-cols-3 ${showCacheDiagnostics ? 'xl:grid-cols-7' : 'lg:grid-cols-6'} gap-3 px-4 pt-4 pb-2`}>
                        <MetricCard
                            icon={cacheStatusDisplay.color === '#b91c1c' ? AlertCircle : CheckCircle2}
                            label="Cache Status"
                            value={
                                <span style={{ color: cacheStatusDisplay.color }}>
                                    {cacheStatusDisplay.label}
                                </span>
                            }
                            sub={cacheCreatedTime ? ticksToDate(cacheCreatedTime) : undefined}
                            color={cacheStatusDisplay.color}
                        />
                        {showCacheDiagnostics && (
                            <MetricCard
                                icon={PackageOpen}
                                label="Cache Delivery"
                                value={cacheMetrics
                                    ? cacheMetrics.cacheFormat === 'v2'
                                        ? <span title="v2 / gzip" style={{ color: '#15803d' }}>Optimized</span>
                                        : <span title="v1 / legacy base64" style={{ color: '#b45309' }}>Compatibility mode</span>
                                    : 'Not loaded'}
                                sub={cacheMetrics
                                    ? cacheMetrics.cacheFormat === 'v2'
                                        ? 'Verified compressed cache'
                                        : (
                                            <span title={`Fallback reason: ${cacheMetrics.fallbackReason || 'v2 unavailable'}`}>
                                                Optimized cache unavailable
                                            </span>
                                        )
                                    : undefined}
                                color={cacheMetrics?.cacheFormat === 'v1' ? '#b45309' : '#15803d'}
                            />
                        )}
                        <MetricCard
                            icon={Hash}
                            label="Total Items"
                            value={cacheMetrics ? <AnimatedNumber end={cacheMetrics.totalCount} /> : '—'}
                            sub={cacheMetrics ? <><AnimatedNumber end={cacheMetrics.familyCount} /> families</> : undefined}
                            color="#6366f1"
                        />
                        <MetricCard
                            icon={HardDrive}
                            label="Cache Size"
                            value={cacheMetrics
                                ? cacheMetrics.sizeMb < 1
                                    ? <><AnimatedNumber end={Math.round(cacheMetrics.sizeMb * 1024)} /> KB</>
                                    : <><AnimatedNumber end={parseFloat(cacheMetrics.sizeMb.toFixed(1)) * 10} formatter={n => (n / 10).toFixed(1)} /> MB</>
                                : '—'}
                            color="#0ea5e9"
                        />
                        <MetricCard
                            icon={Clock}
                            label="Download Time"
                            value={cacheMetrics ? formatDownloadTime(cacheMetrics.downloadTimeMs) : '—'}
                            color="#f59e0b"
                        />
                        <MetricCard
                            icon={FolderTree}
                            label="Categories"
                            value={cacheMetrics ? <AnimatedNumber end={cacheMetrics.categoryCount} /> : '—'}
                            color="#8b5cf6"
                        />
                        <MetricCard
                            icon={AppWindow}
                            label="Applications"
                            value={cacheMetrics ? <AnimatedNumber end={cacheMetrics.applicationCount} /> : '—'}
                            color="#ec4899"
                        />
                    </div>

                    {/* Server-side regen summary. Populated once the LibraryCache
                        timer function has written its status blob for this
                        environment. Complements the client-measured metric cards
                        above with what the backend actually produced: run time,
                        base64 vs gzip blob sizes, node count at regen time, and
                        manifest health. Silent when no summary is available yet. */}
                    {showCacheDiagnostics && (() => {
                        const runResult = library.data?.Library?.lastCacheRunResult;
                        if (!runResult) return null;
                        // Prefer the all-content row (software == null) — that
                        // matches the "library cache" concept users see. Per-
                        // software rows are a subset and would understate totals.
                        const primary = runResult.blobs?.find(b => b?.software == null) ?? runResult.blobs?.[0];
                        const durationSec = runResult.durationMs != null
                            ? (Number(runResult.durationMs) / 1000).toFixed(1)
                            : null;
                        const completedAtStr = runResult.completedAt as unknown as string | undefined;
                        const completedAt = completedAtStr ? new Date(completedAtStr) : null;
                        const relTime = (() => {
                            if (!completedAt || isNaN(completedAt.getTime())) return null;
                            const diff = Math.floor((Date.now() - completedAt.getTime()) / 1000);
                            if (diff < 60) return 'just now';
                            if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
                            if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
                            if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
                            return completedAt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
                        })();
                        const hasError = !!runResult.error;
                        const manifestOk = primary?.manifestOk ?? false;
                        const statusIconColor = hasError ? '#b91c1c' : manifestOk ? '#15803d' : '#b45309';
                        const StatusIcon = hasError ? AlertCircle : manifestOk ? CheckCircle2 : AlertCircle;
                        const statusLabel = hasError ? 'failed' : manifestOk ? 'manifest ok' : 'manifest missing';
                        return (
                            <div
                                className="px-4 py-1.5 text-xs flex items-center gap-2 flex-wrap border-b"
                                style={{ borderColor: 'var(--border-default)', color: 'var(--text-tertiary)' }}
                                title="Most recent server-side cache regeneration outcome for this library"
                            >
                                <Server size={12} />
                                <span style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>Server regen</span>
                                {relTime && <span>· {relTime}</span>}
                                {durationSec && <span>· {durationSec}s</span>}
                                {primary && (
                                    <>
                                        <span title="v1 = legacy base64 blob, v2 = gzipped raw JSON">· v2 {formatBytes(Number(primary.gzipBytes))} / v1 {formatBytes(Number(primary.base64Bytes))}</span>
                                        <span>· {primary.nodeCount.toLocaleString()} items</span>
                                    </>
                                )}
                                <span className="inline-flex items-center gap-1" style={{ color: statusIconColor }}>
                                    · <StatusIcon size={12} /> {statusLabel}
                                </span>
                                {hasError && (
                                    <span className="ml-2" style={{ color: '#b91c1c' }} title={runResult.error ?? undefined}>
                                        {runResult.error}
                                    </span>
                                )}
                            </div>
                        );
                    })()}

                    <div className="px-4 py-2.5 border-b flex items-center gap-3 flex-wrap" style={{ borderColor: 'var(--border-default)' }}>
                        <input
                            type="text"
                            placeholder="Search contents..."
                            className="input-base w-full max-w-sm"
                            value={searchText}
                            onChange={e => setSearchText(e.target.value)}
                        />
                        <div className="flex items-center gap-1.5">
                            {Object.entries(BADGE_CONFIG).map(([key, config]) => {
                                const Icon = config.icon;
                                const isActive = activityFilter.has(key);
                                return (
                                    <button
                                        key={key}
                                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium transition-all duration-150"
                                        style={{
                                            backgroundColor: isActive ? config.bgColor : 'transparent',
                                            color: isActive ? config.color : 'var(--text-tertiary)',
                                            border: `1px solid ${isActive ? config.color : 'var(--border-default)'}`,
                                            cursor: 'pointer',
                                            fontFamily: 'inherit',
                                            opacity: isActive ? 1 : 0.7,
                                        }}
                                        onClick={() => {
                                            setActivityFilter(prev => {
                                                const next = new Set(prev);
                                                if (next.has(key)) next.delete(key);
                                                else next.add(key);
                                                return next;
                                            });
                                        }}
                                    >
                                        <Icon size={11} />
                                        {key.charAt(0).toUpperCase() + key.slice(1)}
                                    </button>
                                );
                            })}
                            {activityFilter.size > 0 && (
                                <button
                                    className="text-xs px-1.5 py-1 rounded-full font-medium"
                                    style={{ color: 'var(--text-tertiary)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                                    onClick={() => setActivityFilter(new Set())}
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="flex-1 overflow-auto">
                        <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ backgroundColor: 'var(--surface-secondary)', borderBottom: '1px solid var(--border-default)' }}>
                                    {columns.map(col => (
                                        <th
                                            key={col}
                                            className="px-3 py-2.5 text-left font-medium cursor-pointer select-none"
                                            style={{ color: 'var(--text-secondary)' }}
                                            onClick={() => handleSort(col)}
                                        >
                                            {col === 'TypeMark' ? 'Type Mark' : col === 'IfcExportAs' ? 'IFC Export As' : col}
                                            {sortField === col && (sortDir === 'asc' ? ' ↑' : ' ↓')}
                                        </th>
                                    ))}
                                    <th className="px-3 py-2.5 text-left font-medium" style={{ color: 'var(--text-secondary)', width: '100px' }}>Activity</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sorted.map((row, i) => (
                                    <tr
                                        key={i}
                                        className="transition-colors duration-150"
                                        style={{ borderBottom: '1px solid var(--border-default)', cursor: row._contentId ? 'pointer' : 'default' }}
                                        onClick={() => row._contentId && setSelectedContentId(row._contentId)}
                                        onMouseOver={(e) => { if (row._contentId) e.currentTarget.style.backgroundColor = 'var(--bg-hover, var(--surface-secondary))'; }}
                                        onMouseOut={(e) => { e.currentTarget.style.backgroundColor = ''; }}
                                    >
                                        {columns.map(col => (
                                            <td key={col} className="px-3 py-2" style={{ color: 'var(--text-primary)' }}>{row[col]}</td>
                                        ))}
                                        <td className="px-3 py-2">
                                            {(() => {
                                                const category = row._contentId ? historyBadgeMap.get(row._contentId) : null;
                                                if (!category) return null;
                                                const config = BADGE_CONFIG[category];
                                                if (!config) return null;
                                                const Icon = config.icon;
                                                return (
                                                    <button
                                                        className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded font-medium"
                                                        style={{ backgroundColor: config.bgColor, color: config.color, border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                                                        onClick={(e) => { e.stopPropagation(); setOpenWithComments(true); setSelectedContentId(row._contentId); }}
                                                    >
                                                        <Icon size={12} />
                                                        {category.charAt(0).toUpperCase() + category.slice(1)}
                                                    </button>
                                                );
                                            })()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {sorted.length === 0 && (
                            <div className="text-center py-10" style={{ color: 'var(--text-tertiary)' }}>No content data found</div>
                        )}
                    </div>
                    <div className="px-4 py-2 text-xs border-t" style={{ borderColor: 'var(--border-default)', color: 'var(--text-secondary)' }}>
                        {sorted.length} items
                    </div>
                </>
            )}

            {selectedContentId && (
                <ContentDetailDialog
                    contentId={selectedContentId}
                    clientId={clientId || ""}
                    visible={!!selectedContentId}
                    defaultCommentsOpen={openWithComments}
                    onClose={() => { setSelectedContentId(null); setOpenWithComments(false); searchParams.delete('contentId'); searchParams.delete('contentTab'); setSearchParams(searchParams); }}
                />
            )}
        </div>
    );
};
const CopyMetaByNameButton = ({
    nodes,
    selectedIds,
    hasNextPage,
    libraryId,
    onDone,
}: {
    nodes: any[];
    selectedIds: string[];
    hasNextPage: boolean;
    libraryId: string | undefined;
    onDone: () => void;
}) => {
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState<{ copied: number; skipped: number } | null>(null);
    const [assignTags] = useAssignContentsTagsMutation();
    const [assignClassifications] = useAssignContentsClassificationsMutation();

    const handleCopy = async () => {
        if (selectedIds.length === 0) {
            alert('Select one or more content items first, then click Copy Meta by Name.');
            return;
        }
        if (hasNextPage) {
            alert('Not all content is loaded yet. Enable Parameter Search to load all pages first, then try again.');
            return;
        }
        if (!nodes?.length) return;
        setRunning(true);
        setResult(null);

        const selectedSet = new Set(selectedIds);
        // Build a name → donor map from non-selected nodes
        const donorByName = new Map<string, any>();
        for (const n of nodes) {
            if (selectedSet.has(n.id)) continue;
            const key = (n.name ?? '').toLowerCase().trim();
            if (!key) continue;
            const existing = donorByName.get(key);
            const nScore = (n.tagIds?.length ?? 0) + (n.classificationIds?.length ?? 0);
            const existingScore = existing ? (existing.tagIds?.length ?? 0) + (existing.classificationIds?.length ?? 0) : -1;
            if (nScore > existingScore) donorByName.set(key, n);
        }

        // Group selected targets by name, look up donor
        const tagBatches = new Map<string, string[]>();   // donorTagIds key → targetIds
        const classBatches = new Map<string, string[]>(); // donorClassIds key → targetIds
        let skipped = 0;

        for (const n of nodes) {
            if (!selectedSet.has(n.id)) continue;
            const key = (n.name ?? '').toLowerCase().trim();
            const donor = key ? donorByName.get(key) : undefined;
            if (!donor) { skipped++; continue; }
            const donorTagIds: string[] = donor.tagIds ?? [];
            const donorClassIds: string[] = donor.classificationIds ?? [];
            if (!donorTagIds.length && !donorClassIds.length) { skipped++; continue; }
            if (donorTagIds.length) {
                const batchKey = donorTagIds.slice().sort().join(',');
                if (!tagBatches.has(batchKey)) tagBatches.set(batchKey, []);
                tagBatches.get(batchKey)!.push(n.id);
            }
            if (donorClassIds.length) {
                const batchKey = donorClassIds.slice().sort().join(',');
                if (!classBatches.has(batchKey)) classBatches.set(batchKey, []);
                classBatches.get(batchKey)!.push(n.id);
            }
        }

        for (const [batchKey, targetIds] of Array.from(tagBatches.entries())) {
            const tagIds = batchKey.split(',');
            await assignTags({ variables: { libraryId, ids: targetIds, tagIds, overwrite: false } });
        }
        for (const [batchKey, targetIds] of Array.from(classBatches.entries())) {
            const classificationIds = batchKey.split(',');
            await assignClassifications({ variables: { libraryId, ids: targetIds, classificationIds, overwrite: false } });
        }

        const copied = selectedIds.length - skipped;
        setRunning(false);
        setResult({ copied, skipped });
        if (copied > 0) onDone();
    };

    return (
        <div className="flex items-center gap-2">
            <button
                className="btn btn-ghost text-sm"
                onClick={handleCopy}
                disabled={running || selectedIds.length === 0}
                title={selectedIds.length === 0 ? 'Select content items to copy tags & classifications from matching names' : 'Copy tags & classifications from non-selected items with matching names'}
            >
                {running ? <Loader2 size={15} className="animate-spin" /> : <Copy size={15} />}
                Copy Meta by Name
                {selectedIds.length > 0 && <span className="ml-1 text-xs opacity-70">({selectedIds.length})</span>}
            </button>
            {result && (
                <span className="text-xs" style={{ color: result.copied > 0 ? 'var(--success)' : 'var(--text-tertiary)' }}>
                    {result.copied > 0 ? `✓ Updated ${result.copied} item${result.copied !== 1 ? 's' : ''}` : 'Nothing to copy'}
                    {result.skipped > 0 ? `, ${result.skipped} skipped (no match)` : ''}
                </span>
            )}
        </div>
    );
};

const ContentsTab = ({ isAdmin }: { isAdmin: boolean; }) => {
    const { clientId, libraryId } = useParams();
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [searchText, setSearchText] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [createContentVisible, setCreateContentVisible] = useState(false);
    const [bulkThumbnailVisible, setBulkThumbnailVisible] = useState(false);
    const [deleteContentVisible, setDeleteContentVisible] = useState(false);
    const [assignContentsTagsVisible, setAssignContentsTagsVisible] = useState(false);
    const [assignContentsClassificationsVisible, setAssignContentsClassificationsVisible] = useState(false);
    const [assignContentsGroupsVisible, setAssignContentsGroupsVisible] = useState(false);
    const [moveContentsVisible, setMoveContentsVisible] = useState(false);
    const [mapClassificationVisible, setMapClassificationVisible] = useState(false);
    const [compareContentsVisible, setCompareContentsVisible] = useState(false);
    const [consolidateByName, setConsolidateByName] = useState(false);
    const [tagIds, setTagIds] = useState<string[]>([]);
    const [parameterSearch, setParameterSearch] = useState("");
    const [parameterChip, setParameterChip] = useState<string | null>(null);
    const [parameterValueSearch, setParameterValueSearch] = useState("");
    const parameterInputRef = useRef<HTMLInputElement>(null);
    const [parameterSearchVisible, setParameterSearchVisible] = useState(false);
    const [classificationFilter, setClassificationFilter] = useState<{ name: string; mode: 'missing' | 'has' } | null>(null);
    const [isFetchingAll, setIsFetchingAll] = useState(false);
    const fetchAllInProgressRef = useRef(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const contentDetailId = searchParams.get('contentId');
    const [openWithComments, setOpenWithComments] = useState(false);
    const currentUser = useCurrentUser();
    const { isRealAdmin, isOverrideActive } = useRoleOverride();
    const canDownloadFiles = isRealAdmin && !isOverrideActive;
    const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);
    const [requestContentFileDownload] = useMutation(REQUEST_CONTENT_FILE_DOWNLOAD);
    const [focusMode, setFocusMode] = useState(false);
    const [setRelatedContents] = useSetRelatedContentsMutation();
    const [linkingRelated, setLinkingRelated] = useState(false);
    const [linkRelatedResult, setLinkRelatedResult] = useState<{ linked: number } | null>(null);
    const [sort, setSort] = useState<TableSort | null>({ field: 'name', direction: 'asc' });
    const categoryList = useGetCategoryListQuery();
    const categoryOptions = useMemo(
        () => (categoryList.data?.CategoryList || [])
            .filter((category): category is string => !!category)
            .sort((left, right) => left.localeCompare(right)),
        [categoryList.data],
    );
    const contentWhere = useMemo<ContentFilterInput | undefined>(() => {
        const conditions: ContentFilterInput[] = [];
        const search = searchText.trim();

        if (search) {
            conditions.push({
                or: [
                    { name: { contains: search } },
                    { category: { contains: search } },
                ],
            });
        }
        if (conditions.length === 0) return undefined;
        return conditions.length === 1 ? conditions[0] : { and: conditions };
    }, [searchText]);

    useEffect(() => {
        if (!focusMode) return;
        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setFocusMode(false); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [focusMode]);

    const contentData = useGetDirectContentsQuery({
        variables: {
            tagIds: tagIds,
            libraryId: libraryId,
            where: contentWhere,
            includeFileDownload: false,
            order: createGraphQLOrder<ContentSortInput>(sort),
        },
    });

    const handleFileDownload = async (event: React.MouseEvent, row: any, fileName: string) => {
        event.stopPropagation();
        if (!canDownloadFiles || !row?.id || downloadingFileId) return;

        setDownloadingFileId(row.id);
        try {
            const result = await requestContentFileDownload({ variables: { contentId: row.id } });
            const downloadUrl = result.data?.Content?.requestFileDownload;
            if (!downloadUrl) throw new Error('The server did not return a content file download URL.');
            const response = await fetch(downloadUrl);
            if (!response.ok) throw new Error(`Download failed (${response.status}).`);
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = objectUrl;
            anchor.download = fileName;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            URL.revokeObjectURL(objectUrl);
        } catch (error) {
            console.error('Unable to download content file:', error);
        } finally {
            setDownloadingFileId(null);
        }
    };

    const historyBadgesData = useGetContentHistoryBadgesQuery({
        variables: { libraryId: libraryId },
    });
    // Auto-paginate to fetch all history badge pages
    useEffect(() => {
        const pageInfo = historyBadgesData.data?.ContentVersionList?.pageInfo;
        if (pageInfo?.hasNextPage && pageInfo.endCursor) {
            historyBadgesData.fetchMore({
                variables: { libraryId, after: pageInfo.endCursor },
                updateQuery: (prev, { fetchMoreResult }) => {
                    if (!fetchMoreResult?.ContentVersionList?.nodes) return prev;
                    return {
                        ...prev,
                        ContentVersionList: {
                            ...fetchMoreResult.ContentVersionList,
                            nodes: [
                                ...(prev.ContentVersionList?.nodes || []),
                                ...fetchMoreResult.ContentVersionList.nodes,
                            ],
                        },
                    };
                },
            });
        }
    }, [historyBadgesData.data]);

    // Query tags for fallback resolution of tagIds -> category key
    const badgeTagsQuery = useGetTagListWithTypeQuery({ variables: { contains: '', type: 'Feedback' } });

    // Build a map: contentId → most recent category tag (Bug, Request, Feature, Comment, Resolved)
    const BADGE_CONFIG: Record<string, { color: string; bgColor: string; icon: typeof Bug }> = {
        bug:      { color: 'var(--danger)',  bgColor: 'var(--danger-soft, #fef2f2)',  icon: Bug },
        request:  { color: 'var(--info)',    bgColor: 'var(--info-soft, #eff6ff)',    icon: HelpCircle },
        feature:  { color: 'var(--success)', bgColor: 'var(--success-soft, #f0fdf4)', icon: Lightbulb },
        comment:  { color: 'var(--accent)',  bgColor: 'var(--accent-soft)',           icon: MessageCircle },
        resolved: { color: 'var(--success)', bgColor: 'var(--success-soft, #f0fdf4)', icon: CircleCheckBig },
    };

    // Map tag ID → category key for fallback resolution
    const badgeTagIdToKeyMap = useMemo<Record<string, string>>(() => {
        const map: Record<string, string> = {};
        const nodes = badgeTagsQuery.data?.TagList?.nodes;
        if (!nodes) return map;
        for (const tag of nodes) {
            const key = tag?.name?.toLowerCase();
            if (key && BADGE_CONFIG[key] && tag?.id) {
                map[tag.id] = key;
            }
        }
        return map;
    }, [badgeTagsQuery.data]);

    const historyBadgeMap = useMemo(() => {
        const map = new Map<string, string>();
        const nodes = historyBadgesData.data?.ContentVersionList?.nodes;
        if (!nodes) return map;
        for (const version of nodes) {
            if (!version?.contentId || !version.histories) continue;
            // Find the most recent history entry that has a recognised category tag
            let latestTime = -1;
            let latestCategory: string | null = null;
            for (const h of version.histories) {
                if (!h) continue;
                const time = h.createdTime ? Number(h.createdTime) : 0;
                // Try resolved tags first
                if (h.tags) {
                    for (const tag of h.tags) {
                        const key = tag?.name?.toLowerCase();
                        if (key && BADGE_CONFIG[key] && tag?.type === 'Feedback' && time > latestTime) {
                            latestTime = time;
                            latestCategory = key;
                        }
                    }
                }
                // Fallback to tagIds
                if (!latestCategory || time > latestTime) {
                    if ((h as any).tagIds) {
                        for (const tid of (h as any).tagIds) {
                            const key = tid ? badgeTagIdToKeyMap[tid] : null;
                            if (key && time > latestTime) {
                                latestTime = time;
                                latestCategory = key;
                            }
                        }
                    }
                }
            }
            if (latestCategory) {
                map.set(version.contentId, latestCategory);
            }
        }
        return map;
    }, [historyBadgesData.data, badgeTagIdToKeyMap]);

    // Client-side filters need every page so their results are exhaustive.
    useEffect(() => {
        const requiresAllContents = parameterSearchVisible || !!categoryFilter;
        if (!requiresAllContents) {
            fetchAllInProgressRef.current = false;
            setIsFetchingAll(false);
            return;
        }
        if (fetchAllInProgressRef.current) return;
        const pageInfo = contentData.data?.Library?.directContents?.pageInfo;
        if (!pageInfo?.hasNextPage) {
            setIsFetchingAll(false);
            return;
        }
        fetchAllInProgressRef.current = true;
        setIsFetchingAll(true);
        contentData.fetchMore({
            variables: {
                libraryId: libraryId,
                where: contentWhere,
                after: pageInfo.endCursor,
                includeFileDownload: canDownloadFiles,
                order: createGraphQLOrder<ContentSortInput>(sort),
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
                if (!fetchMoreResult) return previousResult;
                const previousContents = previousResult.Library?.directContents?.nodes || [];
                const newContents = fetchMoreResult.Library?.directContents?.nodes || [];
                return {
                    ...previousResult,
                    Library: {
                        id: previousResult.Library?.id,
                        directContents: {
                            nodes: [...previousContents, ...newContents],
                            pageInfo: fetchMoreResult.Library?.directContents?.pageInfo as any
                        }
                    }
                };
            }
        }).then(() => {
            fetchAllInProgressRef.current = false;
            // data change will re-trigger this effect to fetch the next page
        }).catch(() => {
            fetchAllInProgressRef.current = false;
            setIsFetchingAll(false);
        });
    }, [parameterSearchVisible, categoryFilter, contentData.data, contentWhere, sort]);

    const filteredNodes = useMemo(() => {
        let nodes = contentData.data?.Library?.directContents?.nodes as any[] | undefined;
        if (!nodes) return nodes;

        if (categoryFilter) {
            const normalizedCategory = categoryFilter.trim().toLocaleLowerCase();
            nodes = nodes.filter((content: any) =>
                String(content?.category ?? '').trim().toLocaleLowerCase() === normalizedCategory
            );
        }

        // Apply parameter search filter
        const hasChip = parameterChip !== null;
        const hasNameSearch = parameterSearch.trim() !== '';
        const hasValueSearch = parameterValueSearch.trim() !== '';
        if (hasChip || hasNameSearch) {
            const nameFilter = (hasChip ? parameterChip : parameterSearch).toLowerCase();
            const valueFilter = hasValueSearch ? parameterValueSearch.toLowerCase() : null;
            nodes = nodes.filter((content: any) => {
                const types = (content?.types as any[]) || [];
                if (types.length === 0) return false;
                try {
                    const settings = JSON.parse(types[0]?.settings ?? '[]') as Array<{ name?: string; value?: string }>;
                    if (valueFilter) {
                        return settings.some(s =>
                            s.name?.toLowerCase().includes(nameFilter) &&
                            (s.value ?? '').toString().toLowerCase().includes(valueFilter)
                        );
                    }
                    return settings.some(s => s.name?.toLowerCase().includes(nameFilter));
                } catch {
                    return false;
                }
            });
        }

        // Apply classification filter
        if (classificationFilter) {
            const filterName = classificationFilter.name.toLowerCase();
            nodes = nodes.filter((content: any) => {
                const classifications = (content?.classifications as any[]) || [];
                const hasType = classifications.some(c => c?.type?.name?.toLowerCase() === filterName);
                return classificationFilter.mode === 'has' ? hasType : !hasType;
            });
        }

        return nodes;
    }, [contentData.data, categoryFilter, parameterSearch, parameterChip, parameterValueSearch, classificationFilter]);

    // Consolidate rows by name: group by content name, show most recent per name with older application names as badges
    const consolidatedNodes = useMemo(() => {
        if (!consolidateByName || !filteredNodes) return filteredNodes;
        const groups = new Map<string, any[]>();
        for (const node of filteredNodes) {
            const key = (node?.name ?? '').toLowerCase().trim();
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key)!.push(node);
        }
        const result: any[] = [];
        groups.forEach((group) => {
            // Sort by createdTime descending (most recent first)
            group.sort((a: any, b: any) => {
                const aTime = Number(a.createdTime ?? 0);
                const bTime = Number(b.createdTime ?? 0);
                return bTime - aTime;
            });
            const primary = { ...group[0] };
            // Collect all unique application names across the group
            const seen = new Set<string>();
            const allApps: string[] = [];
            for (const n of group) {
                const app = n.applicationName;
                if (app && !seen.has(app)) {
                    seen.add(app);
                    allApps.push(app);
                }
            }
            primary._allApplications = allApps;
            primary._consolidatedIds = group.map((n: any) => n.id);
            // Merge consolidated sibling IDs into relatedContentIds so they show in Related Content
            const siblingIds = group.slice(1).map((n: any) => n.id).filter(Boolean);
            const existingRelated: string[] = (primary.relatedContentIds || []).filter(Boolean);
            const mergedRelated = Array.from(new Set([...existingRelated, ...siblingIds]));
            primary.relatedContentIds = mergedRelated;
            result.push(primary);
        });
        return result;
    }, [consolidateByName, filteredNodes]);

    // Classification stats use base nodes (before classification filter) so the stats don't change when you filter by classification
    const classificationBaseNodes = useMemo(() => {
        let nodes = contentData.data?.Library?.directContents?.nodes as any[] | undefined;
        if (!nodes) return nodes;

        if (categoryFilter) {
            const normalizedCategory = categoryFilter.trim().toLocaleLowerCase();
            nodes = nodes.filter((content: any) =>
                String(content?.category ?? '').trim().toLocaleLowerCase() === normalizedCategory
            );
        }

        const hasChip = parameterChip !== null;
        const hasNameSearch = parameterSearch.trim() !== '';
        const hasValueSearch = parameterValueSearch.trim() !== '';
        if (hasChip || hasNameSearch) {
            const nameFilter = (hasChip ? parameterChip : parameterSearch).toLowerCase();
            const valueFilter = hasValueSearch ? parameterValueSearch.toLowerCase() : null;
            nodes = nodes.filter((content: any) => {
                const types = (content?.types as any[]) || [];
                if (types.length === 0) return false;
                try {
                    const settings = JSON.parse(types[0]?.settings ?? '[]') as Array<{ name?: string; value?: string }>;
                    if (valueFilter) {
                        return settings.some(s =>
                            s.name?.toLowerCase().includes(nameFilter) &&
                            (s.value ?? '').toString().toLowerCase().includes(valueFilter)
                        );
                    }
                    return settings.some(s => s.name?.toLowerCase().includes(nameFilter));
                } catch {
                    return false;
                }
            });
        }
        return nodes;
    }, [contentData.data, categoryFilter, parameterSearch, parameterChip, parameterValueSearch]);

    const classificationStats = useMemo(() => {
        const nodes = classificationBaseNodes;
        if (!nodes || nodes.length === 0) return [];
        const totalCount = nodes.length;
        const typeCountMap = new Map<string, { name: string; count: number }>();
        for (const node of nodes) {
            const classifications = (node?.classifications as any[]) || [];
            const seenTypes = new Set<string>();
            for (const c of classifications) {
                const typeName = c?.type?.name;
                if (typeName && !seenTypes.has(typeName)) {
                    seenTypes.add(typeName);
                    const existing = typeCountMap.get(typeName);
                    if (existing) {
                        existing.count++;
                    } else {
                        typeCountMap.set(typeName, { name: typeName, count: 1 });
                    }
                }
            }
        }
        return Array.from(typeCountMap.values())
            .map(({ name, count }) => ({
                name,
                count,
                total: totalCount,
                percentage: Math.round((count / totalCount) * 100)
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [classificationBaseNodes]);

    const handleOnClose = () => {
        setCreateContentVisible(false);
        setBulkThumbnailVisible(false);
        setDeleteContentVisible(false);
        setAssignContentsTagsVisible(false);
        setAssignContentsClassificationsVisible(false);
        setAssignContentsGroupsVisible(false);
        setMoveContentsVisible(false);
        setMapClassificationVisible(false);
        setCompareContentsVisible(false);
        contentData.refetch();
    };

    const handleFilterChanged = (tags: Tag[]) => {
        tags && setTagIds(tags.map(t => t.id || ""));
    };

    const GroupsCellRenderer = ({ row }: { row: any }) => (
        <div className="flex flex-wrap gap-1">
            {(row?.groups as any[])?.map((g: any, i: number) => (
                <span key={i} className="badge badge-default text-xs">{g.name}</span>
            ))}
        </div>
    );

    return (
        <div className={focusMode ? 'fixed inset-0 z-50 flex flex-col' : 'flex flex-col h-full'} style={focusMode ? { backgroundColor: 'var(--bg-app)' } : undefined}>
            <div className="flex items-center gap-2 px-4 py-2.5 border-b flex-wrap" style={{ borderColor: 'var(--border-default)' }}>
                {focusMode && <span className="text-sm font-semibold mr-1" style={{ color: 'var(--text-primary)' }}>Direct Contents</span>}
                <RefreshButton onRefresh={() => contentData.refetch()} />
                <button
                    className={`btn btn-ghost text-sm${parameterSearchVisible ? ' text-[var(--accent)]' : ''}`}
                    onClick={() => { setParameterSearchVisible(v => !v); if (parameterSearchVisible) { setParameterSearch(''); setParameterChip(null); setParameterValueSearch(''); } }}
                    title="Search by parameter name in content types"
                >
                    <Search size={15} /> Parameter Search
                </button>
                <button
                    className={`btn btn-ghost text-sm${consolidateByName ? ' text-[var(--accent)]' : ''}`}
                    onClick={() => setConsolidateByName(v => !v)}
                    title="Consolidate content with the same name into a single row, showing older application versions as badges"
                >
                    <Layers size={15} /> Consolidate
                </button>
                {consolidateByName && (
                    <button
                        className="btn btn-ghost text-sm"
                        disabled={linkingRelated || !consolidatedNodes?.some((n: any) => n._consolidatedIds?.length > 1)}
                        onClick={async () => {
                            if (!consolidatedNodes) return;
                            setLinkingRelated(true);
                            setLinkRelatedResult(null);
                            let linked = 0;
                            // Build a lookup of existing relatedContentIds from loaded data
                            const allNodes = contentData.data?.Library?.directContents?.nodes as any[] || [];
                            const existingRelatedMap = new Map<string, Set<string>>();
                            for (const n of allNodes) {
                                if (n?.id) {
                                    const existing = (n.relatedContentIds || []).filter(Boolean) as string[];
                                    existingRelatedMap.set(n.id, new Set(existing));
                                }
                            }
                            for (const row of consolidatedNodes) {
                                const ids: string[] = row._consolidatedIds || [];
                                if (ids.length <= 1) continue;
                                // For each content in the group, merge the others into its existing related IDs
                                for (const id of ids) {
                                    const existing = existingRelatedMap.get(id) || new Set<string>();
                                    const otherIds = ids.filter((oid: string) => oid !== id);
                                    const merged = new Set(existing);
                                    for (const oid of otherIds) merged.add(oid);
                                    // Only call mutation if there are new IDs to add
                                    if (merged.size > existing.size) {
                                        await setRelatedContents({ variables: { id, ids: Array.from(merged) } });
                                    }
                                }
                                linked++;
                            }
                            setLinkingRelated(false);
                            setLinkRelatedResult({ linked });
                        }}
                        title="Persist consolidated groups as Related Content in the backend"
                    >
                        {linkingRelated ? <Loader2 size={15} className="animate-spin" /> : <Layers size={15} />}
                        Link as Related
                    </button>
                )}
                {linkRelatedResult && (
                    <span className="text-xs" style={{ color: 'var(--success)' }}>
                        ✓ Linked {linkRelatedResult.linked} group{linkRelatedResult.linked !== 1 ? 's' : ''}
                    </span>
                )}
                <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={isAdmin}>
                    <button className="btn btn-ghost text-sm" onClick={() => setCreateContentVisible(true)}>
                        <Plus size={15} /> New Content
                    </button>
                    <button className="btn btn-ghost text-sm" onClick={() => setBulkThumbnailVisible(true)}>
                        <ImageIcon size={15} /> Bulk Upload Thumbnails
                    </button>
                    <button className="btn btn-ghost text-sm" onClick={() => setMapClassificationVisible(true)}>
                        <ClipboardPaste size={15} /> Map Classification
                    </button>
                    <CopyMetaByNameButton
                        nodes={contentData.data?.Library?.directContents?.nodes as any[] ?? []}
                        selectedIds={selectedIds}
                        hasNextPage={!!contentData.data?.Library?.directContents?.pageInfo?.hasNextPage}
                        libraryId={libraryId}
                        onDone={() => contentData.refetch()}
                    />
                    {selectedIds.length > 0 && (
                        <>
                            <div className="w-px h-5 mx-1" style={{ backgroundColor: 'var(--border-default)' }} />
                            <button className="btn btn-ghost text-sm" onClick={() => setAssignContentsTagsVisible(true)}>
                                <TagIcon size={15} /> Assign Tags
                            </button>
                            <button className="btn btn-ghost text-sm" onClick={() => setAssignContentsClassificationsVisible(true)}>
                                <Layers size={15} /> Manage Classifications
                            </button>
                            <button className="btn btn-ghost text-sm" onClick={() => setAssignContentsGroupsVisible(true)}>
                                <FolderPlus size={15} /> Assign Groups
                            </button>
                            <button className="btn btn-ghost text-sm" onClick={() => setMoveContentsVisible(true)}>
                                <ArrowRight size={15} /> Move
                            </button>
                            <button className="btn btn-ghost text-sm" style={{ color: 'var(--danger)' }} onClick={() => setDeleteContentVisible(true)}>
                                <Trash2 size={15} /> Delete
                            </button>
                        </>
                    )}
                </ShowOnRole>
                <div className="ml-auto flex gap-2 items-center">
                    <button
                        className="btn btn-ghost text-sm"
                        onClick={() => {
                            // Export logic
                            const nodes = contentData.data?.Library?.directContents?.nodes || [];
                            // Gather all unique classification types
                            const allTypes = Array.from(new Set(nodes.flatMap((row: any) => (row.classifications || []).filter(Boolean).map((c: any) => c.type?.name).filter(Boolean))));
                            // Build header
                            const baseColumns = ['name', 'category', 'application', 'typeMark'];
                            const columns = ['Name', 'Category', 'Application', 'TypeMark', ...allTypes];
                            // Build rows, filter out empty rows
                            const rows = nodes
                                .filter((row: any) => row && (row.name || row.category || row.application || row.typeMark || (row.classifications && row.classifications.length > 0)))
                                .map((row: any) => {
                                    const base = [row.name, row.category, row.application, row.typeMark];
                                    const classMap = Object.fromEntries((row.classifications || []).filter(Boolean).map((c: any) => [c.type?.name, c.name]));
                                    const classVals = allTypes.map(type => classMap[type] || '');
                                    return [...base, ...classVals];
                                });
                            // CSV escape
                            const escape = (val: any) => (val == null ? '' : (`"${String(val).replace(/"/g, '""')}"`));
                            const csv = [columns.map(escape).join(','), ...rows.map(r => r.map(escape).join(','))].join('\r\n');
                            // Download
                            const blob = new Blob([csv], { type: 'text/csv' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = 'direct-contents-export.csv';
                            document.body.appendChild(a);
                            a.click();
                            setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
                        }}
                        title="Export all rows (CSV)"
                    >
                        <FileText size={15} /> Export
                    </button>
                    <button
                        className="btn btn-ghost text-sm"
                        onClick={() => setCompareContentsVisible(true)}
                        title="Compare contents with another library"
                    >
                        <GitCompareArrows size={15} /> Compare
                    </button>
                    <button
                        className={`btn btn-ghost text-sm${focusMode ? ' text-[var(--accent)]' : ''}`}
                        onClick={() => setFocusMode(v => !v)}
                        title={focusMode ? 'Exit Focus Mode (Esc)' : 'Focus Mode – expand to full screen'}
                    >
                        {focusMode ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
                        {focusMode ? 'Exit Focus' : 'Focus Mode'}
                    </button>
                </div>
            </div>
            {parameterSearchVisible && (
                <div className="flex items-center gap-2 px-4 py-2 border-b" style={{ borderColor: 'var(--border-default)', backgroundColor: 'var(--surface-secondary)' }}>
                    <Search size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                    <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                        {parameterChip ? 'Parameter value:' : 'Parameter name:'}
                    </span>
                    <div
                        className="flex items-center gap-1 flex-1 max-w-lg px-2 py-1 rounded border text-sm"
                        style={{
                            borderColor: 'var(--border-default)',
                            backgroundColor: 'var(--bg-input, var(--surface-primary))',
                            minHeight: '34px',
                            cursor: 'text',
                        }}
                        onClick={() => parameterInputRef.current?.focus()}
                    >
                        {parameterChip && (
                            <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap"
                                style={{
                                    backgroundColor: 'var(--accent-soft, rgba(59,130,246,0.1))',
                                    color: 'var(--accent)',
                                    border: '1px solid var(--accent)',
                                    lineHeight: '1.5',
                                }}
                            >
                                {parameterChip}
                                <button
                                    type="button"
                                    className="flex items-center justify-center w-4 h-4 p-0 rounded-full border-none bg-transparent cursor-pointer"
                                    style={{ color: 'inherit', opacity: 0.7 }}
                                    onClick={e => { e.stopPropagation(); setParameterChip(null); setParameterValueSearch(''); setTimeout(() => parameterInputRef.current?.focus(), 0); }}
                                    tabIndex={-1}
                                    title="Remove parameter filter"
                                >
                                    <X size={11} />
                                </button>
                            </span>
                        )}
                        <input
                            ref={parameterInputRef}
                            type="text"
                            className="flex-1 border-none outline-none bg-transparent text-sm"
                            style={{ color: 'var(--text-primary)', minWidth: '80px', padding: '2px 0' }}
                            placeholder={
                                parameterChip
                                    ? 'Search value… (Backspace to remove parameter)'
                                    : 'e.g. RatedCurrent, Manufacturer… (Tab ⇥ to lock in)'
                            }
                            value={parameterChip ? parameterValueSearch : parameterSearch}
                            onChange={e => parameterChip ? setParameterValueSearch(e.target.value) : setParameterSearch(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Tab' && !parameterChip) {
                                    e.preventDefault();
                                    if (parameterSearch.trim()) {
                                        setParameterChip(parameterSearch.trim());
                                        setParameterSearch('');
                                        setParameterValueSearch('');
                                        setTimeout(() => parameterInputRef.current?.focus(), 0);
                                    }
                                } else if (e.key === 'Backspace' && parameterChip && parameterValueSearch === '') {
                                    setParameterChip(null);
                                    setParameterValueSearch('');
                                }
                            }}
                            autoFocus
                            disabled={isFetchingAll}
                        />
                    </div>
                    {(parameterSearch || parameterChip || parameterValueSearch) && (
                        <button
                            type="button"
                            className="flex items-center justify-center w-5 h-5 p-0 rounded border-none bg-transparent cursor-pointer"
                            style={{ color: 'var(--text-tertiary)' }}
                            onClick={() => { setParameterSearch(''); setParameterChip(null); setParameterValueSearch(''); setTimeout(() => parameterInputRef.current?.focus(), 0); }}
                            tabIndex={-1}
                            title="Clear all search terms"
                        >
                            <X size={14} />
                        </button>
                    )}
                    {isFetchingAll ? (
                        <span className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>
                            <Loader2 size={12} className="animate-spin" />
                            Loading all pages… ({contentData.data?.Library?.directContents?.nodes?.length ?? 0} so far)
                        </span>
                    ) : (parameterSearch || parameterChip) ? (
                        <span className="text-xs" style={{ color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>
                            {filteredNodes?.length ?? 0} of {contentData.data?.Library?.directContents?.nodes?.length ?? 0}
                        </span>
                    ) : (
                        <span className="text-xs" style={{ color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>
                            {contentData.data?.Library?.directContents?.nodes?.length ?? 0} loaded
                        </span>
                    )}
                </div>
            )}
            {classificationStats.length > 0 && (
                <div className="flex items-center gap-2 px-4 py-2 border-b flex-wrap" style={{ borderColor: 'var(--border-default)', backgroundColor: 'var(--surface-secondary)' }}>
                    <Layers size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                    <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Classification Complete:</span>
                    {classificationStats.map(stat => {
                        const isActive = classificationFilter?.name === stat.name;
                        const mode = isActive ? classificationFilter!.mode : null;
                        return (
                            <button
                                key={stat.name}
                                type="button"
                                className="flex items-center gap-1.5 rounded-md px-2 py-1 border transition-all duration-150"
                                style={{
                                    backgroundColor: isActive
                                        ? mode === 'missing' ? 'rgba(239,68,68,0.10)' : 'rgba(34,197,94,0.10)'
                                        : 'var(--bg-app)',
                                    borderColor: isActive
                                        ? mode === 'missing' ? '#ef4444' : '#15803d'
                                        : 'transparent',
                                    cursor: 'pointer',
                                }}
                                onClick={() => {
                                    if (!isActive) {
                                        setClassificationFilter({ name: stat.name, mode: 'missing' });
                                    } else if (mode === 'missing') {
                                        setClassificationFilter({ name: stat.name, mode: 'has' });
                                    } else {
                                        setClassificationFilter(null);
                                    }
                                }}
                                title={
                                    !isActive
                                        ? `Click to show content missing "${stat.name}"`
                                        : mode === 'missing'
                                            ? `Showing content WITHOUT "${stat.name}" — click for content WITH it`
                                            : `Showing content WITH "${stat.name}" — click to clear filter`
                                }
                            >
                                {isActive && (
                                    <span className="text-xs font-semibold" style={{ color: mode === 'missing' ? '#ef4444' : '#15803d', whiteSpace: 'nowrap' }}>
                                        {mode === 'missing' ? '✗' : '✓'}
                                    </span>
                                )}
                                <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{stat.name}</span>
                                <div className="w-16 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--surface-300)' }}>
                                    <div
                                        className="h-full rounded-full transition-all duration-500"
                                        style={{
                                            width: `${stat.percentage}%`,
                                            backgroundColor: stat.percentage === 100 ? '#15803d' : stat.percentage >= 75 ? '#0ea5e9' : stat.percentage >= 50 ? '#f59e0b' : '#ef4444'
                                        }}
                                    />
                                </div>
                                <span className="text-xs font-semibold" style={{
                                    color: stat.percentage === 100 ? '#15803d' : stat.percentage >= 75 ? '#0ea5e9' : stat.percentage >= 50 ? '#f59e0b' : '#ef4444',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {stat.percentage}%
                                </span>
                                <span className="text-xs" style={{ color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>({stat.count}/{stat.total})</span>
                                {isActive && (
                                    <span
                                        role="button"
                                        tabIndex={-1}
                                        className="ml-0.5 flex items-center justify-center w-4 h-4 rounded-full"
                                        style={{ color: 'var(--text-tertiary)', cursor: 'pointer' }}
                                        onClick={e => { e.stopPropagation(); setClassificationFilter(null); }}
                                        title="Clear filter"
                                    >
                                        <X size={11} />
                                    </span>
                                )}
                            </button>
                        );
                    })}
                    {classificationFilter && (
                        <span className="text-xs font-medium ml-1" style={{ color: classificationFilter.mode === 'missing' ? '#ef4444' : '#15803d', whiteSpace: 'nowrap' }}>
                            {classificationFilter.mode === 'missing'
                                ? `Showing content without "${classificationFilter.name}"`
                                : `Showing content with "${classificationFilter.name}"`
                            }
                            <button
                                type="button"
                                className="btn btn-ghost text-xs py-0 px-1.5 ml-1"
                                onClick={() => setClassificationFilter(null)}
                            >Clear</button>
                        </span>
                    )}
                </div>
            )}
            <div className="flex-1 overflow-hidden">
                <DataTableNew
                    sortingMode="server"
                    sort={sort}
                    onSortChange={setSort}
                    showTags
                    isLoading={contentData.loading || isFetchingAll}
                    data={consolidatedNodes as any[]}
                    onRowClick={(row) => setSearchParams({ contentId: row.id, contentTab: 'direct-contents' })}
                    onSearchChanged={(value) => setSearchText(value)}
                    onSelected={(value) => setSelectedIds(value)}
                    filterable
                    onFilterChanged={handleFilterChanged}
                    toolbarControls={(
                        <label className="flex items-center gap-2 text-sm shrink-0" style={{ color: 'var(--text-secondary)' }}>
                            <span>Category</span>
                            <select
                                className="input-base w-48"
                                style={{ cursor: 'pointer' }}
                                value={categoryFilter}
                                onChange={(event) => setCategoryFilter(event.target.value)}
                                disabled={categoryList.loading}
                                aria-label="Filter direct contents by category"
                            >
                                <option value="">All categories</option>
                                {categoryOptions.map(category => (
                                    <option key={category} value={category}>{category}</option>
                                ))}
                            </select>
                        </label>
                    )}
                    columns={[
                        {
                            id: 'thumbnail',
                            name: 'Thumbnail',
                            width: '86px',
                            render: (row: any) => row?.thumbnail?.downloadUrl ? (
                                <img
                                    src={row.thumbnail.downloadUrl}
                                    alt={`${row.name || 'Content'} thumbnail`}
                                    className="rounded object-cover border"
                                    style={{ width: 44, height: 36, borderColor: 'var(--border-default)', backgroundColor: 'var(--bg-subtle)' }}
                                />
                            ) : <span style={{ color: 'var(--text-tertiary)' }}>—</span>,
                        },
                        {
                            id: 'file',
                            name: 'File',
                            width: '58px',
                            render: (row: any) => {
                                if (!row?.file) return null;
                                const rawExtension = String(row.file.extension || '');
                                const extension = rawExtension && !rawExtension.startsWith('.') ? `.${rawExtension}` : rawExtension;
                                const fileName = `${row.file.name || 'File'}${extension && !String(row.file.name || '').endsWith(extension) ? extension : ''}`;
                                if (!canDownloadFiles) {
                                    return (
                                        <span className="inline-flex items-center" title={fileName} aria-label={`File: ${fileName}`}>
                                            <FileText size={17} style={{ color: 'var(--accent-text)' }} />
                                        </span>
                                    );
                                }

                                const isDownloadingFile = downloadingFileId === row.id;
                                return (
                                    <button
                                        type="button"
                                        className="btn-icon p-1 rounded inline-flex items-center"
                                        onClick={(event) => handleFileDownload(event, row, fileName)}
                                        disabled={isDownloadingFile}
                                        title={`Download ${fileName}`}
                                        aria-label={`Download ${fileName}`}
                                    >
                                        {isDownloadingFile ? <Loader2 size={17} className="animate-spin" /> : <FileText size={17} style={{ color: 'var(--accent-text)' }} />}
                                    </button>
                                );
                            },
                        },
                        { id: 'category', name: 'Category', sortField: 'category' },
                        {
                            id: 'description',
                            name: 'Description',
                            width: '260px',
                            render: (row: any) => (
                                <span className="block truncate" style={{ maxWidth: '240px' }} title={row.description || ''}>
                                    {row.description || <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                                </span>
                            ),
                        },
                        { id: 'applicationName', name: 'Application', render: (row: any) => {
                            const apps: string[] | undefined = row._allApplications;
                            if (!apps || apps.length <= 1) {
                                return <span>{row.applicationName}</span>;
                            }
                            return (
                                <div className="flex flex-col items-start gap-0.5">
                                    {apps.map((app: string, i: number) => (
                                        <span
                                            key={i}
                                            className="inline-flex items-center text-xs px-1.5 py-0.5 rounded-full font-medium"
                                            style={{
                                                backgroundColor: i === 0
                                                    ? 'var(--accent-soft, rgba(59,130,246,0.1))'
                                                    : 'var(--surface-300, rgba(0,0,0,0.06))',
                                                color: i === 0
                                                    ? 'var(--accent, #3b82f6)'
                                                    : 'var(--text-tertiary)',
                                                whiteSpace: 'nowrap',
                                            }}
                                            title={i === 0 ? 'Most recent application' : `Earlier version: ${app}`}
                                        >
                                            {app}
                                        </span>
                                    ))}
                                </div>
                            );
                        }},
                        ...currentUser?.role !== UserRoleEnum.None ? [{ id: 'usageCount', name: 'Usage' }] : [],
                        { id: 'activity', name: 'Activity', width: '100px', render: (row: any) => {
                            const category = historyBadgeMap.get(row.id);
                            if (!category) return null;
                            const config = BADGE_CONFIG[category];
                            if (!config) return null;
                            const Icon = config.icon;
                            return (
                                <button
                                    className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded font-medium"
                                    style={{ backgroundColor: config.bgColor, color: config.color, border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                                    onClick={(e) => { e.stopPropagation(); setOpenWithComments(true); setSearchParams({ contentId: row.id, contentTab: 'direct-contents' }); }}
                                >
                                    <Icon size={12} />
                                    {category.charAt(0).toUpperCase() + category.slice(1)}
                                </button>
                            );
                        }},
                        { id: 'groups', name: 'Groups', width: '120px', render: (row: any) => <GroupsCellRenderer row={row} /> },
                        { id: 'classifications', name: 'Classifications', width: '280px', render: (row: any) => <ClassificationsCell row={row} /> },
                        { id: 'action', name: '', width: '56px', render: (row: any) => row?.thumbnail?.downloadUrl ? <ImageViewerButton title={row.name || ""} link={row.thumbnail.downloadUrl || ""} /> : null },
                    ]}
                    fetchMore={parameterSearchVisible || categoryFilter ? undefined : () => contentData.data?.Library?.directContents?.pageInfo?.hasNextPage && contentData.fetchMore({
                        variables: {
                            libraryId: libraryId,
                            where: contentWhere,
                            after: contentData.data?.Library?.directContents?.pageInfo.endCursor,
                            includeFileDownload: canDownloadFiles,
                            order: createGraphQLOrder<ContentSortInput>(sort),
                        },
                        updateQuery: (previousResult, { fetchMoreResult }) => {
                            if (!fetchMoreResult) return previousResult;
                            const previousContents = contentData.data?.Library?.directContents?.nodes || [];
                            const newContents = fetchMoreResult.Library?.directContents?.nodes || [];
                            return {
                                ...previousResult,
                                Library: {
                                    id: contentData.data?.Library?.id,
                                    directContents: {
                                        nodes: [...previousContents, ...newContents],
                                        pageInfo: fetchMoreResult.Library?.directContents?.pageInfo as any
                                    }
                                }
                            };
                        }
                    })}
                />
            </div>
            {createContentVisible && <NewContentDialog libraryId={libraryId || ""} visible={createContentVisible} onClose={handleOnClose} />}
            {bulkThumbnailVisible && <BulkThumbnailUploadDialog libraryId={libraryId || ""} visible={bulkThumbnailVisible} onClose={handleOnClose} />}
            {deleteContentVisible && <DeleteContentsDialog libraryId={libraryId || ""} ids={selectedIds} visible={deleteContentVisible} onClose={handleOnClose} />}
            {assignContentsTagsVisible && <AddContentsTagsDialog libraryId={libraryId || ""} ids={selectedIds} visible={assignContentsTagsVisible} onClose={handleOnClose} />}
            {assignContentsClassificationsVisible && <AddContentsClassificationsDialog libraryId={libraryId || ""} ids={selectedIds} visible={assignContentsClassificationsVisible} onClose={handleOnClose} />}
            {assignContentsGroupsVisible && <AddContentsGroupsDialog libraryId={libraryId || ""} ids={selectedIds} visible={assignContentsGroupsVisible} onClose={handleOnClose} />}
            {moveContentsVisible && <MoveContentsDialog libraryId={libraryId || ""} ids={selectedIds} visible={moveContentsVisible} onClose={handleOnClose} />}
            {mapClassificationVisible && <MapClassificationDialog libraryId={libraryId || ""} visible={mapClassificationVisible} onClose={handleOnClose} />}
            {compareContentsVisible && <CompareContentsDialog libraryId={libraryId || ""} currentContents={(contentData.data?.Library?.directContents?.nodes || []) as any[]} visible={compareContentsVisible} onClose={handleOnClose} />}
            {contentDetailId && <ContentDetailDialog contentId={contentDetailId} clientId={clientId || ""} visible={!!contentDetailId} defaultCommentsOpen={openWithComments} onClose={() => { searchParams.delete('contentId'); searchParams.delete('contentTab'); setSearchParams(searchParams); setOpenWithComments(false); if (!parameterSearchVisible) contentData.refetch(); }} />}
        </div>
    );
};
const SourceLibrariesTab = ({ isAdmin, onCreateCache }: { isAdmin: boolean; onCreateCache?: () => void; }) => {
    const { clientId, libraryId } = useParams();
    const navigate = useNavigate();
    const [selectedSourceLibraryIds, setSelectedSourceLibraryIds] = useState<string[]>([]);
    const [searchSourceLibraries, setSearchSourceLibraries] = useState("");
    const [inheritLibraryVisible, setInheritLibraryVisible] = useState(false);
    const [unInheritLibrariesVisible, setUnInheritLibrariesVisible] = useState(false);
    const [registerContentsVisible, setRegisterContentsVisible] = useState(false);
    const [selectedSourceLibraryId, setSelectedSourceLibraryId] = useState<string>("");

    const sourceLibraries = useGetInheritedLibrariesQuery({
        variables: {
            id: libraryId,
            contains: searchSourceLibraries
        }
    });

    const unregisteredContents = useGetUnregisteredContentsQuery({
        variables: {
            id: libraryId,
            contains: "",
            category: "",
            tagIds: []
        }
    });

    const unregisteredCountByLibrary = React.useMemo(() => {
        const counts: Record<string, number> = {};
        const nodes = unregisteredContents.data?.Library?.unregisteredContents?.nodes;
        if (nodes) {
            for (const node of nodes) {
                if (node?.libraryId) {
                    counts[node.libraryId] = (counts[node.libraryId] || 0) + 1;
                }
            }
        }
        return counts;
    }, [unregisteredContents.data]);

    const libraries = sourceLibraries.data?.Library?.inheritedLibraries?.nodes as any[] || [];

    const handleUnInheritLibrariesOnClose = () => {
        setUnInheritLibrariesVisible(false);
        setSelectedSourceLibraryIds([]);
        sourceLibraries.refetch();
    };

    const handleInheritLibraryClose = () => {
        setInheritLibraryVisible(false);
        sourceLibraries.refetch();
    };

    return (
        <div className="flex flex-col h-full">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b" style={{ borderColor: 'var(--border-default)' }}>
                <RefreshButton onRefresh={() => { sourceLibraries.refetch(); unregisteredContents.refetch(); }} />
                <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={isAdmin}>
                    <button className="btn btn-ghost text-sm" onClick={() => setInheritLibraryVisible(true)}>
                        <Plus size={15} /> Add Source
                    </button>
                    {selectedSourceLibraryIds.length > 0 && (
                        <>
                            <div className="w-px h-5 mx-1" style={{ backgroundColor: 'var(--border-default)' }} />
                            <button className="btn btn-ghost text-sm" style={{ color: 'var(--danger)' }} onClick={() => setUnInheritLibrariesVisible(true)}>
                                <Trash2 size={15} /> Remove ({selectedSourceLibraryIds.length})
                            </button>
                        </>
                    )}
                </ShowOnRole>
            </div>
            <div className="flex-1 overflow-auto p-4">
                {(sourceLibraries.loading || unregisteredContents.loading) ? (
                    <SpinnerNew />
                ) : !libraries || libraries.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16" style={{ color: 'var(--text-tertiary)' }}>
                        <PackageOpen size={32} className="mb-3 opacity-50" />
                        <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>No source libraries</p>
                        <p className="text-xs mt-1">Inherit from a library to share its content</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 animate-fade-in">
                        {libraries.map((lib: any) => {
                            const count = unregisteredCountByLibrary[lib.id] ?? 0;
                            const isSelected = selectedSourceLibraryIds.includes(lib.id);
                            return (
                                <div
                                    key={lib.id}
                                    className="card p-5 group relative cursor-pointer transition-all"
                                    style={{
                                        borderColor: isSelected ? 'var(--accent)' : undefined,
                                        backgroundColor: isSelected ? 'var(--accent-soft)' : undefined,
                                    }}
                                    onClick={() => { setSelectedSourceLibraryId(lib.id); setRegisterContentsVisible(true); }}
                                >
                                    {/* Selection checkbox */}
                                    <div className="absolute top-4 right-4" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => {
                                                setSelectedSourceLibraryIds(prev =>
                                                    prev.includes(lib.id) ? prev.filter(i => i !== lib.id) : [...prev, lib.id]
                                                );
                                            }}
                                            className="w-4 h-4 rounded cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
                                            style={{
                                                accentColor: 'var(--accent)',
                                                opacity: isSelected ? 1 : undefined,
                                            }}
                                        />
                                    </div>

                                    <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                                        style={{ backgroundColor: 'var(--accent-soft)' }}>
                                        <LibraryIcon size={20} style={{ color: 'var(--accent)' }} />
                                    </div>

                                    <span
                                        className="text-sm font-semibold hover:underline block mb-1"
                                        style={{ color: 'var(--text-primary)' }}
                                    >
                                        {lib.name}
                                    </span>

                                    {lib.description && (
                                        <p className="text-xs mb-3 line-clamp-2" style={{ color: 'var(--text-secondary)' }}>
                                            {lib.description}
                                        </p>
                                    )}

                                    {/* Unregistered count */}
                                    <div className="flex items-center gap-1.5 mt-auto pt-3 border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                                        <Layers size={12} style={{ color: count > 0 ? 'var(--accent)' : 'var(--text-tertiary)' }} />
                                        <span className="text-xs" style={{ color: count > 0 ? 'var(--accent-text)' : 'var(--text-tertiary)' }}>
                                            {count} unregistered content{count !== 1 ? 's' : ''}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            {unInheritLibrariesVisible && <UnInheritLibraryDialog libraryId={libraryId || ""} ids={selectedSourceLibraryIds} visible={unInheritLibrariesVisible} onClose={handleUnInheritLibrariesOnClose} />}
            {inheritLibraryVisible && <InheritLibraryDialog clientId={clientId} libraryId={libraryId} visible={inheritLibraryVisible} onClose={handleInheritLibraryClose} />}
            {registerContentsVisible && <RegisterContentsDialog visible={registerContentsVisible} clientId={clientId || ""} libraryId={libraryId || ""} sourceLibraryId={selectedSourceLibraryId} sourceLibraryName={libraries?.find((l: any) => l.id === selectedSourceLibraryId)?.name} onRemove={() => { setSelectedSourceLibraryIds([selectedSourceLibraryId]); setUnInheritLibrariesVisible(true); }} onClose={() => { setRegisterContentsVisible(false); sourceLibraries.refetch(); unregisteredContents.refetch(); }} onCloseAndUpdateCache={onCreateCache} />}
        </div>
    );
};
const CustomContentsTab = ({ isAdmin }: { isAdmin: boolean; }) => {
    const { libraryId } = useParams();
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [addCustomContentsVisible, setAddCustomeContentsVisible] = useState(false);
    const [deleteCustomContentsVisible, setDeleteCustomContentsVisible] = useState(false);
    const customContents = useGetLibraryCustomContentsQuery({
        variables: {
            id: libraryId
        }
    });

    const handleOnClose = () => {
        setAddCustomeContentsVisible(false);
        setDeleteCustomContentsVisible(false);
        customContents.refetch();
    };

    const EditCustomContentButtonRenderer = ({ row }: { row: any }) => {
        const [editCustomContentSettingsVisible, setEditCustomContentSettingsVisible] = useState(false);
        const handleOnClose = () => {
            setEditCustomContentSettingsVisible(false);
            customContents.refetch();
        };
        return (
            <div className="flex justify-center">
                <button className="btn btn-ghost p-1" onClick={() => setEditCustomContentSettingsVisible(true)}>
                    <Pencil size={14} />
                </button>
                {editCustomContentSettingsVisible && <EditCustomContentSettingsDialog
                    settings={row.customSettings}
                    customContentId={row.id}
                    libraryId={libraryId || ""}
                    visible={editCustomContentSettingsVisible}
                    onClose={handleOnClose}
                />}
            </div>
        );
    };

    return (
        <div className="flex flex-col h-full">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b" style={{ borderColor: 'var(--border-default)' }}>
                <RefreshButton onRefresh={() => customContents.refetch()} />
                <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={isAdmin}>
                    <button className="btn btn-ghost text-sm" onClick={() => setAddCustomeContentsVisible(true)}>
                        <Plus size={15} /> Add Contents
                    </button>
                    {selectedIds.length > 0 && (
                        <>
                            <div className="w-px h-5 mx-1" style={{ backgroundColor: 'var(--border-default)' }} />
                            <button className="btn btn-ghost text-sm" style={{ color: 'var(--danger)' }} onClick={() => setDeleteCustomContentsVisible(true)}>
                                <Trash2 size={15} /> Delete
                            </button>
                        </>
                    )}
                </ShowOnRole>
            </div>
            <div className="flex-1 overflow-hidden">
                <DataTableNew
                    sortingMode="client"
                    isLoading={customContents.loading}
                    data={customContents.data?.Library?.customContents as any[]}
                    showName={false}
                    searchable={false}
                    onSelected={(value) => setSelectedIds(value)}
                    columns={[
                        { id: 'content.name', name: 'Name', sortable: true },
                        { id: '', name: 'Custom Settings', width: '140px', render: (row: any) => <EditCustomContentButtonRenderer row={row} /> }
                    ]}
                />
            </div>
            {addCustomContentsVisible && <AddCustomContentsDialog
                visible={addCustomContentsVisible}
                onClose={handleOnClose}
                libraryId={libraryId || ""} />}
            {deleteCustomContentsVisible && <DeleteCustomContentsDialog
                visible={deleteCustomContentsVisible}
                onClose={handleOnClose}
                libraryId={libraryId || ""}
                ids={selectedIds}
            />}
        </div>
    );
};
const AdminsTab = () => {
    const { libraryId } = useParams();
    const [newClientAdminVisible, setNewClientAdminVisible] = useState(false);
    const library = useGetLibraryQuery({
        variables: {
            id: libraryId
        }
    });

    const handleNewLibraryAdminClose = () => {
        library.refetch();
        setNewClientAdminVisible(false);
    };

    const [deleteAdminId, setDeleteAdminId] = useState<string | null>(null);
    const [selectedAdminIds, setSelectedAdminIds] = useState<string[]>([]);
    const [deleteSelectedVisible, setDeleteSelectedVisible] = useState(false);

    const admins = (library.data?.Library?.admins as any[]) || [];
    const isAdmin = library.data?.Library?.isAdmin || false;

    const getInitials = (name?: string, email?: string) => {
        if (name) {
            const parts = name.trim().split(/\s+/);
            return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name.substring(0, 2).toUpperCase();
        }
        return email ? email.substring(0, 2).toUpperCase() : '??';
    };

    return (
        <div className="flex flex-col h-full">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b" style={{ borderColor: 'var(--border-default)' }}>
                <RefreshButton onRefresh={() => library.refetch()} />
                <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={isAdmin}>
                    <button className="btn btn-ghost text-sm" onClick={() => setNewClientAdminVisible(true)}>
                        <Plus size={15} /> New Admin
                    </button>
                    {selectedAdminIds.length > 0 && (
                        <>
                            <div className="w-px h-5 mx-1" style={{ backgroundColor: 'var(--border-default)' }} />
                            <button className="btn btn-ghost text-sm" style={{ color: 'var(--danger)' }} onClick={() => setDeleteSelectedVisible(true)}>
                                <Trash2 size={15} /> Remove ({selectedAdminIds.length})
                            </button>
                        </>
                    )}
                </ShowOnRole>
            </div>
            <div className="flex-1 overflow-auto p-4">
                {library.loading ? (
                    <SpinnerNew />
                ) : admins.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16" style={{ color: 'var(--text-tertiary)' }}>
                        <Shield size={32} className="mb-3 opacity-50" />
                        <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>No admins</p>
                        <p className="text-xs mt-1">Add an admin to manage this library</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 animate-fade-in">
                        {admins.map((admin: any) => {
                            const isSelected = selectedAdminIds.includes(admin.id);
                            return (
                                <div
                                    key={admin.id}
                                    className="card p-5 group relative transition-all"
                                    style={{
                                        borderColor: isSelected ? 'var(--accent)' : undefined,
                                        backgroundColor: isSelected ? 'var(--accent-soft)' : undefined,
                                    }}
                                >
                                    {/* Selection checkbox + delete */}
                                    <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={isAdmin}>
                                        <div className="absolute top-4 right-4 flex items-center gap-2">
                                            <button
                                                className="btn btn-ghost p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                                style={{ color: 'var(--danger)' }}
                                                onClick={() => setDeleteAdminId(admin.id)}
                                                title="Remove admin"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => {
                                                    setSelectedAdminIds(prev =>
                                                        prev.includes(admin.id) ? prev.filter(i => i !== admin.id) : [...prev, admin.id]
                                                    );
                                                }}
                                                className="w-4 h-4 rounded cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
                                                style={{
                                                    accentColor: 'var(--accent)',
                                                    opacity: isSelected ? 1 : undefined,
                                                }}
                                            />
                                        </div>
                                    </ShowOnRole>

                                    <div className="w-10 h-10 rounded-full flex items-center justify-center mb-3 text-sm font-semibold"
                                        style={{ backgroundColor: 'var(--accent-soft)', color: 'var(--accent)' }}>
                                        {getInitials(admin.name, admin.email)}
                                    </div>

                                    {admin.name && (
                                        <p className="text-sm font-semibold mb-0.5" style={{ color: 'var(--text-primary)' }}>
                                            {admin.name}
                                        </p>
                                    )}
                                    <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                                        {admin.email}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            {newClientAdminVisible && <NewLibraryAdminDialog visible={newClientAdminVisible} onClose={handleNewLibraryAdminClose} libraryId={libraryId ?? ""} />}
            {deleteAdminId && <DeleteLibraryAdminDialog visible={!!deleteAdminId} libraryId={libraryId || ""} ids={[deleteAdminId]} onClose={() => { setDeleteAdminId(null); library.refetch(); }} />}
            {deleteSelectedVisible && <DeleteLibraryAdminDialog visible={deleteSelectedVisible} libraryId={libraryId || ""} ids={selectedAdminIds} onClose={() => { setDeleteSelectedVisible(false); setSelectedAdminIds([]); library.refetch(); }} />}
        </div>
    );
};
const ProjectsTab = () => {
    const { libraryId } = useParams();
    const [searchText, setSearchText] = useState("");
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [tagIds, setTagIds] = useState<string[]>([]);
    const [projectSort, setProjectSort] = useState<TableSort | null>({ field: 'name', direction: 'asc' });

    const result = useGetLibraryProjectsQuery({
        variables: {
            tagIds: tagIds,
            libraryId: libraryId,
            contains: searchText,
            order: createGraphQLOrder<ProjectSortInput>(projectSort),
        }
    });

    const handleOnFilterChanged = (tags: Tag[]) => {
        tags && setTagIds(tags.map(t => t.id || ""));
    };

    return (
        <div className="flex flex-col h-full">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b" style={{ borderColor: 'var(--border-default)' }}>
                <RefreshButton onRefresh={() => result.refetch()} />
            </div>
            <div className="flex-1 overflow-hidden">
                <DataTableNew
                    sortingMode="server"
                    sort={projectSort}
                    onSortChange={setProjectSort}
                    isLoading={result.loading}
                    data={result.data?.ProjectList?.nodes as any[]}
                    link="/Projects"
                    onSearchChanged={(value) => setSearchText(value)}
                    onSelected={(ids) => setSelectedIds(ids)}
                    filterable
                    onFilterChanged={handleOnFilterChanged}
                    columns={[{ id: 'client.name', name: 'Client' }]}
                    fetchMore={() => result.data?.ProjectList?.pageInfo?.hasNextPage && result.fetchMore({
                        variables: {
                            libraryId: libraryId,
                            contains: searchText,
                            after: result.data?.ProjectList?.pageInfo.endCursor,
                            order: createGraphQLOrder<ProjectSortInput>(projectSort),
                        },
                        updateQuery: (previousResult, { fetchMoreResult }) => {
                            if (!fetchMoreResult) return previousResult;
                            const previousContents = result.data?.ProjectList?.nodes || [];
                            const newContents = fetchMoreResult.ProjectList?.nodes || [];
                            return {
                                ...previousResult,
                                ProjectList: {
                                    nodes: [...previousContents, ...newContents],
                                    pageInfo: fetchMoreResult.ProjectList?.pageInfo as any
                                }
                            };
                        }
                    })}
                />
            </div>
        </div>
    );
};
type LibraryProps = {
    style?: CSSProperties | undefined,
    children?: ReactNode | undefined,
};

const Library = (props: LibraryProps) => {
    const { libraryId } = useParams();
    const location = useLocation();
    const [librarySearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState<string>(() => {
        if (librarySearchParams.get('contentId')) return librarySearchParams.get('contentTab') === 'direct-contents' ? 'direct-contents' : 'contents';
        return (location.state as any)?.tab || 'contents';
    });
    const [createCacheLoading, setCreateCacheLoading] = useState(false);
    const [resetUsageLoading, setResetUsageLoading] = useState(false);
    const [resetUsageMessage, setResetUsageMessage] = useState<string | null>(null);
    const [moveLibraryVisible, setMoveLibraryVisible] = useState(false);
    const [assignTagsVisible, setAssignTagsVisible] = useState(false);
    const [deleteTagVisible, setDeleteTagVisible] = useState(false);
    const [selectedTagId, setSelectedTagId] = useState('');
    const [isEditingName, setIsEditingName] = useState(false);
    const [editNameValue, setEditNameValue] = useState('');
    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const [editDescriptionValue, setEditDescriptionValue] = useState('');
    const [renameLibrary, renameLibraryResult] = useRenameLibraryMutation();
    const [editLibraryDescription, editLibraryDescriptionResult] = useEditLibraryDescriptionMutation();
    const [resetContentUsageCounts] = useMutation(RESET_LIBRARY_CONTENT_USAGE_COUNTS);

    useEffect(() => {
        const tab = (location.state as any)?.tab;
        if (tab) setActiveTab(tab);
    }, [location.state]);

    useEffect(() => {
        if (librarySearchParams.get('contentId')) {
            setActiveTab(librarySearchParams.get('contentTab') === 'direct-contents' ? 'direct-contents' : 'contents');
        }
    }, [librarySearchParams]);

    const httpClient = useLoadHttpClient();
    const library = useGetLibraryQuery({
        fetchPolicy: 'network-only',
        variables: {
            id: libraryId
        },
    });
    const handleCreateCache = async () => {
        const cacheStatus = library.data?.Library?.cacheStatus;
        if (libraryId &&
            (
                cacheStatus === LibraryCacheStatus.Failed ||
                cacheStatus === LibraryCacheStatus.Succeeded ||
                cacheStatus === LibraryCacheStatus.None ||
                !cacheStatus
            )
        ) {
            setCreateCacheLoading(true);
            const form = new FormData();
            form.append("libraryId", libraryId || "");
            let postSucceeded = false;
            try {
                await httpClient.post(LIBRARY_CACHE_URI, form);
                postSucceeded = true;
            } catch (err) {
                console.error("Failed to trigger library cache creation:", err);
                setCreateCacheLoading(false);
                return;
            }
            if (!postSucceeded) {
                setCreateCacheLoading(false);
                return;
            }
            // Poll until cache status settles (no longer Running/Pending/null)
            const maxAttempts = 20;
            const delayMs = 3000;
            for (let i = 0; i < maxAttempts; i++) {
                await new Promise(resolve => setTimeout(resolve, delayMs));
                const result = await library.refetch();
                const status = result.data?.Library?.cacheStatus;
                if (
                    status === LibraryCacheStatus.Succeeded ||
                    status === LibraryCacheStatus.Failed ||
                    status === LibraryCacheStatus.None
                ) {
                    break;
                }
            }
            setCreateCacheLoading(false);
        }
    };

    const handleResetUsageCounts = async () => {
        if (!libraryId || !window.confirm('Reset usage counts to zero for every content item in this library? This cannot be undone.')) return;

        setResetUsageLoading(true);
        setResetUsageMessage(null);
        try {
            const result = await resetContentUsageCounts({
                variables: { libraryId },
                refetchQueries: ['getDirectContents'],
                awaitRefetchQueries: true,
            });
            const count = result.data?.Library?.resetContentUsageCounts ?? 0;
            setResetUsageMessage(`${count} content usage count${count === 1 ? '' : 's'} reset.`);
        } catch (error) {
            console.error('Failed to reset content usage counts:', error);
            setResetUsageMessage('Unable to reset content usage counts.');
        } finally {
            setResetUsageLoading(false);
        }
    };
    const onCloseHandle = () => {
        setMoveLibraryVisible(false);
        setAssignTagsVisible(false);
        setDeleteTagVisible(false);
        library.refetch();
    };

    const handleRemoveTag = (tagId: string) => {
        setSelectedTagId(tagId);
        setDeleteTagVisible(true);
    };

    const handleSaveName = async () => {
        const name = editNameValue.trim();
        if (!name || !libraryId) return;
        try {
            await renameLibrary({ variables: { id: libraryId, name } });
            setIsEditingName(false);
            await library.refetch();
        } catch {
            // handled by the mutation result
        }
    };

    const handleSaveDescription = async () => {
        if (!libraryId) return;
        try {
            await editLibraryDescription({
                variables: { id: libraryId, description: editDescriptionValue.trim() },
            });
            setIsEditingDescription(false);
            await library.refetch();
        } catch {
            // handled by the mutation result
        }
    };

    const isAdmin = library.data?.Library?.isAdmin || false;
    const currentUser = useCurrentUser();
    const isGlobalAdmin = currentUser?.role === UserRoleEnum.Admin;
    const client = useContext(ClientContext);
    const isClientAdmin = client?.data?.Client?.isAdmin || false;
    const { isOverrideActive } = useRoleOverride();
    // When "View as User" is active, ignore entity-level admin flags
    const canSeeAdminFeatures = isOverrideActive ? false : (isGlobalAdmin || isClientAdmin || isAdmin);

    // If the user can't see admin features, redirect away from admin-only tabs
    useEffect(() => {
        if (!canSeeAdminFeatures && (activeTab === 'direct-contents' || activeTab === 'source-libraries')) {
            setActiveTab('contents');
        }
    }, [canSeeAdminFeatures, activeTab]);

    const tabs = [
        { id: 'contents', label: 'Contents', icon: FileText },
        ...(canSeeAdminFeatures ? [
            { id: 'direct-contents', label: 'Direct Contents', icon: FileInput },
            { id: 'source-libraries', label: 'Source Libraries', icon: LibraryIcon },
        ] : []),
        { id: 'projects', label: 'Projects', icon: FolderKanban },
        { id: 'admins', label: 'Admins', icon: Shield },
    ];

    return (
        <div className="flex flex-col h-full">
            {/* Admin actions toolbar */}
            {canSeeAdminFeatures && (
                <div className="flex items-center gap-2 px-4 py-2.5 border-b" style={{ borderColor: 'var(--border-default)' }}>
                    <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={isAdmin}>
                        <button className="btn btn-ghost text-sm" disabled={createCacheLoading} onClick={handleCreateCache}>
                            {createCacheLoading ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
                            {createCacheLoading ? 'Creating Cache...' : 'Create Cache'}
                        </button>
                    </ShowOnRole>
                    {(isGlobalAdmin || isClientAdmin) && (
                        <button className="btn btn-ghost text-sm" onClick={() => setMoveLibraryVisible(true)}>
                            <ArrowRight size={15} /> Move
                        </button>
                    )}
                    {isGlobalAdmin && !isOverrideActive && (
                        <button className="btn btn-ghost text-sm" disabled={resetUsageLoading} onClick={handleResetUsageCounts}>
                            {resetUsageLoading ? <Loader2 size={15} className="animate-spin" /> : <RotateCcw size={15} />}
                            {resetUsageLoading ? 'Resetting Usage...' : 'Reset Usage'}
                        </button>
                    )}
                    <button className="btn btn-ghost text-sm" onClick={() => setAssignTagsVisible(true)}>
                        <TagIcon size={15} /> Add Tags
                    </button>
                    {resetUsageMessage && (
                        <span className="text-xs" style={{ color: resetUsageMessage.startsWith('Unable') ? 'var(--danger)' : 'var(--success)' }}>
                            {resetUsageMessage}
                        </span>
                    )}
                </div>
            )}

            {/* Essentials */}
            <EssentialsNew
                persistenceKey="library-details"
                className="mx-4 mt-3"
                isAdmin={canSeeAdminFeatures}
                tags={library.data?.Library?.tags?.filter((tag): tag is Tag => !!tag).map(tag => tag as Tag)}
                onRemoveTag={handleRemoveTag}
                rows={[
                    { title: "Name", value: isEditingName ? (
                        <div className="flex items-center gap-1.5">
                            <input
                                className="text-sm px-2 py-0.5 rounded"
                                style={{ border: '1px solid var(--border-default)', background: 'var(--bg-input)', color: 'var(--text-primary)', outline: 'none', minWidth: 220 }}
                                value={editNameValue}
                                onChange={(event) => setEditNameValue(event.target.value)}
                                onKeyDown={(event) => { if (event.key === 'Enter') handleSaveName(); if (event.key === 'Escape') setIsEditingName(false); }}
                                autoFocus
                            />
                            <button className="btn-icon p-0.5 rounded" onClick={handleSaveName} disabled={renameLibraryResult.loading} title="Save library name"><CheckCircle2 size={13} /></button>
                            <button className="btn-icon p-0.5 rounded" onClick={() => setIsEditingName(false)} title="Cancel"><X size={13} /></button>
                        </div>
                    ) : (
                        <span className="flex items-center gap-1.5 group">
                            <span>{library.data?.Library?.name || ''}</span>
                            {canSeeAdminFeatures && (
                                <button
                                    className="btn-icon p-0.5 rounded opacity-0 group-hover:opacity-100"
                                    style={{ transition: 'opacity 0.15s' }}
                                    onClick={() => { setEditNameValue(library.data?.Library?.name || ''); setIsEditingName(true); }}
                                    title="Edit library name"
                                >
                                    <Pencil size={12} />
                                </button>
                            )}
                        </span>
                    ) },
                    { title: "Cache Status", value: (() => {
                        const status = library?.data?.Library?.cacheStatus;
                        const statusConfig: Record<string, { label: string; color: string; bg: string }> = {
                            [LibraryCacheStatus.Succeeded]: { label: 'Created', color: '#15803d', bg: '#dcfce7' },
                            [LibraryCacheStatus.Running]:   { label: 'Running', color: '#b45309', bg: '#fef3c7' },
                            [LibraryCacheStatus.Pending]:   { label: 'Pending', color: '#b45309', bg: '#fef3c7' },
                            [LibraryCacheStatus.Failed]:    { label: 'Failed',  color: '#b91c1c', bg: '#fee2e2' },
                            [LibraryCacheStatus.None]:      { label: 'None',    color: '#6b7280', bg: '#f3f4f6' },
                        };
                        const config = status ? statusConfig[status] : null;
                        if (!config) return '';
                        return (
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full" style={{ color: config.color, backgroundColor: config.bg }}>
                                {createCacheLoading && <Loader2 size={12} className="animate-spin" />}
                                {config.label}
                            </span>
                        );
                    })() },
                    { title: "Cache Created Time", value: ticksToDate(library?.data?.Library?.cacheCreatedTime || "") },
                    { title: "Description", value: isEditingDescription ? (
                        <div className="flex items-start gap-1.5">
                            <textarea
                                className="text-sm px-2 py-1 rounded w-full"
                                style={{ border: '1px solid var(--border-default)', background: 'var(--bg-input)', color: 'var(--text-primary)', outline: 'none', minWidth: '260px', minHeight: '64px', resize: 'vertical' }}
                                value={editDescriptionValue}
                                onChange={(event) => setEditDescriptionValue(event.target.value)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Escape') setIsEditingDescription(false);
                                    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) handleSaveDescription();
                                }}
                                autoFocus
                            />
                            <button className="btn-icon p-0.5 rounded" onClick={handleSaveDescription} disabled={editLibraryDescriptionResult.loading} title="Save library description"><CheckCircle2 size={13} /></button>
                            <button className="btn-icon p-0.5 rounded" onClick={() => setIsEditingDescription(false)} title="Cancel"><X size={13} /></button>
                        </div>
                    ) : (
                        <span className="flex items-center gap-1.5 group">
                            <span>{library.data?.Library?.description || <span style={{ color: 'var(--text-tertiary)' }}>No description</span>}</span>
                            {canSeeAdminFeatures && (
                                <button
                                    className="btn-icon p-0.5 rounded opacity-0 group-hover:opacity-100"
                                    style={{ transition: 'opacity 0.15s' }}
                                    onClick={() => { setEditDescriptionValue(library.data?.Library?.description || ''); setIsEditingDescription(true); }}
                                    title="Edit library description"
                                >
                                    <Pencil size={12} />
                                </button>
                            )}
                        </span>
                    ) },
                ]}
            />

            {/* Tab bar */}
            <div className="px-4 pt-3 pb-1">
                <div className="tab-bar" style={{ alignSelf: 'flex-start', display: 'inline-flex' }}>
                    {tabs.map(tab => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={activeTab === tab.id ? 'tab-item-active' : 'tab-item'}
                            >
                                <Icon size={14} strokeWidth={1.5} />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Tab content */}
            <div className="flex-1 overflow-hidden">
                {activeTab === 'contents' && <PreviewTab showCacheDiagnostics={canSeeAdminFeatures} />}
                {activeTab === 'direct-contents' && <ContentsTab isAdmin={canSeeAdminFeatures} />}
                {activeTab === 'source-libraries' && <SourceLibrariesTab isAdmin={canSeeAdminFeatures} onCreateCache={handleCreateCache} />}
                {activeTab === 'projects' && <ProjectsTab />}
                {activeTab === 'admins' && <AdminsTab />}
            </div>

            {moveLibraryVisible && <MoveLibraryDialog visible={moveLibraryVisible} onClose={onCloseHandle} libraryId={library.data?.Library?.id || ""} />}
            {assignTagsVisible && <AddLibrariesTagsDialog visible={assignTagsVisible} onClose={onCloseHandle} ids={[libraryId || '']} />}
            {deleteTagVisible && <DeleteLibraryTagDialog visible={deleteTagVisible} onClose={onCloseHandle} id={libraryId || ''} tagId={selectedTagId} />}
        </div>
    );
};

export default Library;
